import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Tooltip,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Menu,
  ToggleButtonGroup,
  ToggleButton,
  IconButton
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  Inventory as InventoryIcon,
  ShoppingCart as ShoppingCartIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Warning as WarningIcon,
  Download as DownloadIcon,
  FileDownload as FileDownloadIcon,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon,
  Add as AddIcon,
  Update as UpdateIcon,
  CheckCircle as CheckCircleIcon,
  ContentCopy as ContentCopyIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL, API_ENDPOINTS, getApiUrl } from '../config/api';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import {
  parseSalesData,
  matchSalesItems,
  groupProcessedSales,
  buildSalesRegistrationItems,
  parseSupplierReturnData,
  parseSupplierReturnColumns,
  matchSupplierReturnItems,
  buildSupplierReturnItems,
  usesStoreStockForSales,
  type ProcessedSale
} from '../utils/salesRegistration';

// Pajamavimas interfaces
interface ParsedItem {
  barkodas: string;
  pavadinimas: string;
  papildymoKiekis: number;
  savikaina?: number; // optional when user did not paste/enter Savikaina (column empty)
  kaina?: number;
  lineNumber: number;
}

interface StorageItem {
  id: string;
  name: string;
  barkodas?: string;
  prekesKodas?: string;
  category: string;
  quantity: number;
  storeQuantity?: number;
  savikaina?: number;
  pardavimoKaina?: number;
  supplier?: string;
}

interface MatchedItem extends ParsedItem {
  matchedItem: StorageItem | null;
  matchType: 'barkodas' | 'name' | 'none';
  needsPriceUpdate: boolean;
  priceUpdateApplied: boolean;
  wasCreated: boolean; // Track if item was just created via "Naujas produktas"
  pendingCreation: boolean; // New flag: true if marked for creation but not yet created
  originalSavikaina?: number; // Original cost price before update
  originalPardavimoKaina?: number; // Original selling price before update
  savikainaChanged: boolean; // Whether Savikaina actually changed
  pardavimoKainaChanged: boolean; // Whether Pardavimo kaina actually changed
  status: 'matched' | 'unmatched' | 'price_update_needed' | 'ready';
  barcodeMismatch: boolean; // true when barcode provided but not found (even if name match exists)
}

const MONTH_NAMES_LT = ['Sausis', 'Vasaris', 'Kovas', 'Balandis', 'Gegužė', 'Birželis', 'Liepa', 'Rugpjūtis', 'Rugsėjis', 'Spalis', 'Lapkritis', 'Gruodis'];

const Pardavimai: React.FC = () => {
  const { t } = useTranslation();
  const { token, user, hasPermission } = useAuth();
  const [activeTab, setActiveTab] = useState<number>(0);
  
  // Prekyba tab state
  const [prekybaCategory, setPrekybaCategory] = useState<string>('');
  const [salesMonth, setSalesMonth] = useState<Date | null>(new Date());
  const [salesDataText, setSalesDataText] = useState<string>('');
  const [processedSalesGrouped, setProcessedSalesGrouped] = useState<ProcessedSale[]>([]);
  const [processedSalesRaw, setProcessedSalesRaw] = useState<ProcessedSale[]>([]);
  const [prekybaStorageItems, setPrekybaStorageItems] = useState<StorageItem[]>([]);
  const [prekybaLoading, setPrekybaLoading] = useState(false);
  const [prekybaError, setPrekybaError] = useState<string | null>(null);

  // Sales registration log (Pardavimų registravimo žurnalas)
  const [salesRegistrationLog, setSalesRegistrationLog] = useState<Array<{
    id: string;
    registeredAt: string;
    salesMonth: string;
    category: string;
    user: { firstName: string; lastName: string } | null;
    itemCount: number;
    supplier: string | null;
  }>>([]);
  const [salesRegistrationLogLoading, setSalesRegistrationLogLoading] = useState(false);
  const [salesRegistrationLogError, setSalesRegistrationLogError] = useState<string | null>(null);
  const [expandedBatchId, setExpandedBatchId] = useState<string | null>(null);
  const [batchItemsMap, setBatchItemsMap] = useState<Record<string, Array<{
    id: string;
    quantity: number;
    type: string;
    notes: string;
    transactionDate: string;
    item: { name: string; prekesKodas: string | null; category: string; supplier?: string | null } | null;
  }>>>({});

  // Restock log (Pajamavimo žurnalas)
  const [restockLog, setRestockLog] = useState<Array<{
    id: string;
    registeredAt: string;
    category: string;
    supplier: string;
    documentNumber: string | null;
    user: { firstName: string; lastName: string } | null;
    itemCount: number;
  }>>([]);
  const [restockLogLoading, setRestockLogLoading] = useState(false);
  const [restockLogError, setRestockLogError] = useState<string | null>(null);
  const [expandedRestockBatchId, setExpandedRestockBatchId] = useState<string | null>(null);
  const [restockBatchItemsMap, setRestockBatchItemsMap] = useState<Record<string, Array<{
    id: string;
    quantity: number;
    type: string;
    notes: string;
    documentNumber: string | null;
    transactionDate: string;
    item: { name: string; prekesKodas: string | null; category: string } | null;
  }>>>({});

  // Store transfer log (Perkėlimų žurnalas)
  const [storeTransferLog, setStoreTransferLog] = useState<Array<{
    id: string;
    registeredAt: string;
    category: string;
    direction: string;
    notes: string | null;
    user: { firstName: string; lastName: string } | null;
    itemCount: number;
  }>>([]);
  const [storeTransferLogLoading, setStoreTransferLogLoading] = useState(false);
  const [storeTransferLogError, setStoreTransferLogError] = useState<string | null>(null);
  const [expandedStoreTransferBatchId, setExpandedStoreTransferBatchId] = useState<string | null>(null);
  const [storeTransferBatchItemsMap, setStoreTransferBatchItemsMap] = useState<Record<string, Array<{
    id: string;
    quantity: number;
    type: string;
    notes: string;
    transactionDate: string;
    item: { name: string; prekesKodas: string | null; category: string } | null;
  }>>>({});

  // Pajamavimas tab state
  const [pajamavimasCategory, setPajamavimasCategory] = useState<string>('');
  const [pajamavimasSupplier, setPajamavimasSupplier] = useState<string>('');
  const [isNewSupplier, setIsNewSupplier] = useState(false);
  const [newSupplierName, setNewSupplierName] = useState('');
  const [pajamavimasVaztarasitis, setPajamavimasVaztarasitis] = useState('');
  const [pastedData, setPastedData] = useState('');
  const [parsedItems, setParsedItems] = useState<ParsedItem[]>([]);
  const [matchedItems, setMatchedItems] = useState<MatchedItem[]>([]);
  const [storageItems, setStorageItems] = useState<StorageItem[]>([]);
  const [allSuppliers, setAllSuppliers] = useState<string[]>([]);
  const [pajamavimasLoading, setPajamavimasLoading] = useState(false);
  const [pajamavimasError, setPajamavimasError] = useState<string | null>(null);
  const [createItemDialogOpen, setCreateItemDialogOpen] = useState(false);
  const [itemToCreate, setItemToCreate] = useState<ParsedItem | null>(null);
  const [priceMenuAnchor, setPriceMenuAnchor] = useState<{ anchorEl: HTMLElement; lineNumber: number } | null>(null);
  const [pajamavimasInputMode, setPajamavimasInputMode] = useState<'full' | 'columns'>('full');
  const [pajamavimasColBarkodas, setPajamavimasColBarkodas] = useState('');
  const [pajamavimasColPavadinimas, setPajamavimasColPavadinimas] = useState('');
  const [pajamavimasColKiekis, setPajamavimasColKiekis] = useState('');
  const [pajamavimasColSavikaina, setPajamavimasColSavikaina] = useState('');
  const [pajamavimasColKaina, setPajamavimasColKaina] = useState('');
  const pajamavimasColRefs = useRef({
    barkodas: '',
    pavadinimas: '',
    kiekis: '',
    savikaina: '',
    kaina: ''
  });

  // Grąžinimai tab state
  const [grazinimaiCategory, setGrazinimaiCategory] = useState<string>('');
  const [grazinimaiSupplier, setGrazinimaiSupplier] = useState<string>('');
  const [grazinimaiSuppliers, setGrazinimaiSuppliers] = useState<string[]>([]);
  const [returnMonth, setReturnMonth] = useState<Date | null>(new Date());
  const [grazinimaiInputMode, setGrazinimaiInputMode] = useState<'full' | 'columns'>('full');
  const [grazinimaiDataText, setGrazinimaiDataText] = useState<string>('');
  const [grazinimaiColBarkodas, setGrazinimaiColBarkodas] = useState('');
  const [grazinimaiColKiekis, setGrazinimaiColKiekis] = useState('');
  const grazinimaiColRefs = useRef({ barkodas: '', kiekis: '' });
  const [processedReturnsGrouped, setProcessedReturnsGrouped] = useState<ProcessedSale[]>([]);
  const [processedReturnsRaw, setProcessedReturnsRaw] = useState<ProcessedSale[]>([]);
  const [grazinimaiStorageItems, setGrazinimaiStorageItems] = useState<StorageItem[]>([]);
  const [grazinimaiLoading, setGrazinimaiLoading] = useState(false);
  const [grazinimaiError, setGrazinimaiError] = useState<string | null>(null);
  const [supplierReturnLog, setSupplierReturnLog] = useState<Array<{
    id: string;
    registeredAt: string;
    returnMonth: string;
    category: string;
    user: { firstName: string; lastName: string } | null;
    itemCount: number;
    supplier: string | null;
  }>>([]);
  const [supplierReturnLogLoading, setSupplierReturnLogLoading] = useState(false);
  const [supplierReturnLogError, setSupplierReturnLogError] = useState<string | null>(null);
  const [expandedSupplierReturnBatchId, setExpandedSupplierReturnBatchId] = useState<string | null>(null);
  const [supplierReturnBatchItemsMap, setSupplierReturnBatchItemsMap] = useState<Record<string, Array<{
    id: string;
    quantity: number;
    type: string;
    notes: string;
    transactionDate: string;
    item: { name: string; prekesKodas: string | null; category: string; supplier?: string | null } | null;
  }>>>({});

  // Ataskaitos tab state
  const [ataskaitosMonth, setAtaskaitosMonth] = useState<Date | null>(new Date());
  const [ataskaitosCategory, setAtaskaitosCategory] = useState<string>('');
  const [ataskaitosSupplier, setAtaskaitosSupplier] = useState<string>('');
  const [ataskaitosSuppliers, setAtaskaitosSuppliers] = useState<string[]>([]);
  const [ataskaitosReport, setAtaskaitosReport] = useState<any[]>([]);
  const [ataskaitosVaztarasciuSarasas, setAtaskaitosVaztarasciuSarasas] = useState<string[]>([]);
  const [ataskaitosLoading, setAtaskaitosLoading] = useState(false);
  const [ataskaitosError, setAtaskaitosError] = useState<string | null>(null);

  // Prekyba functions
  const fetchPrekybaStorageItems = async (category: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api${API_ENDPOINTS.STORAGE_ITEMS_BY_CATEGORY(category)}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setPrekybaStorageItems(data.items);
        }
      }
    } catch (error) {
      console.error('Error fetching storage items:', error);
      setPrekybaError('Nepavyko užkrauti prekių iš Sandėliukas');
    }
  };

  useEffect(() => {
    if (prekybaCategory) {
      fetchPrekybaStorageItems(prekybaCategory);
    }
  }, [prekybaCategory]);

  useEffect(() => {
    if (ataskaitosCategory) {
      fetchSuppliersForCategory(ataskaitosCategory);
    } else {
      setAtaskaitosSuppliers([]);
    }
  }, [ataskaitosCategory]);

  useEffect(() => {
    if (activeTab === 0) {
      fetchSalesRegistrationLog();
      fetchStoreTransferLog();
    }
  }, [activeTab, prekybaCategory]);

  const fetchRestockLog = async () => {
    setRestockLogLoading(true);
    setRestockLogError(null);
    try {
      const params = new URLSearchParams();
      params.set('limit', '50');
      if (pajamavimasCategory) params.set('category', pajamavimasCategory);
      if (pajamavimasSupplier) params.set('supplier', pajamavimasSupplier);
      const url = `${getApiUrl(API_ENDPOINTS.STORAGE_RESTOCK_LOG)}?${params.toString()}`;
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to load restock log');
      const data = await response.json();
      if (data.success && Array.isArray(data.batches)) {
        setRestockLog(data.batches);
      }
    } catch (e) {
      setRestockLogError(e instanceof Error ? e.message : 'Nepavyko užkrauti žurnalo');
    } finally {
      setRestockLogLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 1) {
      fetchRestockLog();
    }
  }, [activeTab, pajamavimasCategory, pajamavimasSupplier]);

  const fetchGrazinimaiStorageItems = async (category: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api${API_ENDPOINTS.STORAGE_ITEMS_BY_CATEGORY(category)}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setGrazinimaiStorageItems(data.items);
          const suppliers = Array.from(
            new Set(
              (data.items as StorageItem[])
                .map((item) => item.supplier)
                .filter((s): s is string => Boolean(s && String(s).trim()))
            )
          ).sort((a, b) => a.localeCompare(b, 'lt')) as string[];
          setGrazinimaiSuppliers(suppliers);
          setGrazinimaiSupplier((prev) => (prev && suppliers.includes(prev) ? prev : ''));
        }
      }
    } catch (error) {
      console.error('Error fetching storage items for returns:', error);
      setGrazinimaiError('Nepavyko užkrauti prekių iš Sandėliukas');
    }
  };

  useEffect(() => {
    if (grazinimaiCategory) {
      fetchGrazinimaiStorageItems(grazinimaiCategory);
    } else {
      setGrazinimaiStorageItems([]);
      setGrazinimaiSuppliers([]);
      setGrazinimaiSupplier('');
    }
  }, [grazinimaiCategory]);

  useEffect(() => {
    if (activeTab === 2) {
      fetchSupplierReturnLog();
    }
  }, [activeTab, grazinimaiCategory, grazinimaiSupplier]);

  const fetchSupplierReturnLog = async () => {
    setSupplierReturnLogLoading(true);
    setSupplierReturnLogError(null);
    try {
      const params = new URLSearchParams();
      if (grazinimaiCategory) params.set('category', grazinimaiCategory);
      if (grazinimaiSupplier) params.set('supplier', grazinimaiSupplier);
      params.set('limit', '50');
      const url = `${getApiUrl(API_ENDPOINTS.STORAGE_SUPPLIER_RETURN_LOG)}?${params.toString()}`;
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to load log');
      const data = await response.json();
      if (data.success && Array.isArray(data.batches)) {
        setSupplierReturnLog(data.batches);
      }
    } catch (e) {
      setSupplierReturnLogError(e instanceof Error ? e.message : 'Nepavyko užkrauti žurnalo');
    } finally {
      setSupplierReturnLogLoading(false);
    }
  };

  const fetchSupplierReturnBatchItems = async (batchId: string) => {
    if (supplierReturnBatchItemsMap[batchId]) return;
    try {
      const response = await fetch(getApiUrl(API_ENDPOINTS.STORAGE_SUPPLIER_RETURN_LOG_ITEMS(batchId)), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to load items');
      const data = await response.json();
      if (data.success && Array.isArray(data.items)) {
        setSupplierReturnBatchItemsMap(prev => ({ ...prev, [batchId]: data.items }));
      }
    } catch {
      setSupplierReturnBatchItemsMap(prev => ({ ...prev, [batchId]: [] }));
    }
  };

  const handleExpandSupplierReturnBatch = (batchId: string) => {
    if (expandedSupplierReturnBatchId === batchId) {
      setExpandedSupplierReturnBatchId(null);
      return;
    }
    setExpandedSupplierReturnBatchId(batchId);
    fetchSupplierReturnBatchItems(batchId);
  };

  const handleProcessGrazinimaiData = () => {
    if (!grazinimaiCategory) {
      setGrazinimaiError(t('pardavimai.grazinimaiSelectCategory'));
      return;
    }
    if (!grazinimaiSupplier) {
      setGrazinimaiError(t('pardavimai.grazinimaiSelectSupplier'));
      return;
    }
    if (!returnMonth) {
      setGrazinimaiError(t('pardavimai.grazinimaiSelectMonth'));
      return;
    }
    let parsed;
    if (grazinimaiInputMode === 'full') {
      if (!grazinimaiDataText.trim()) {
        setGrazinimaiError(t('pardavimai.grazinimaiPasteRequired'));
        return;
      }
      parsed = parseSupplierReturnData(grazinimaiDataText);
    } else {
      const colBarkodas = grazinimaiColRefs.current.barkodas || grazinimaiColBarkodas;
      const colKiekis = grazinimaiColRefs.current.kiekis || grazinimaiColKiekis;
      if (!colBarkodas.trim() && !colKiekis.trim()) {
        setGrazinimaiError(t('pardavimai.grazinimaiPasteRequired'));
        return;
      }
      parsed = parseSupplierReturnColumns(colBarkodas, colKiekis);
    }
    setGrazinimaiError(null);
    const supplierItems = grazinimaiStorageItems.filter(
      (item) => (item.supplier || '').trim() === grazinimaiSupplier.trim()
    );
    const raw = matchSupplierReturnItems(parsed, supplierItems);
    setProcessedReturnsRaw(raw);
    setProcessedReturnsGrouped(groupProcessedSales(raw));
  };

  const handleRegisterGrazinimai = async () => {
    if (!returnMonth) {
      setGrazinimaiError(t('pardavimai.grazinimaiSelectMonth'));
      return;
    }
    if (!grazinimaiSupplier) {
      setGrazinimaiError(t('pardavimai.grazinimaiSelectSupplier'));
      return;
    }
    const insufficientStock = processedReturnsRaw.filter(ps => ps.status === 'insufficient_stock');
    if (insufficientStock.length > 0) {
      const items = [...new Set(insufficientStock.map(ps => ps.preke))].join(', ');
      setGrazinimaiError(t('pardavimai.grazinimaiInsufficientStock', { items }));
      return;
    }
    const matchedItems = processedReturnsRaw.filter(ps => ps.status === 'matched' && ps.matchedItem);
    if (matchedItems.length === 0) {
      setGrazinimaiError(t('pardavimai.grazinimaiNothingToRegister'));
      return;
    }
    try {
      setGrazinimaiLoading(true);
      setGrazinimaiError(null);
      const items = buildSupplierReturnItems(processedReturnsRaw);
      const response = await fetch(getApiUrl(API_ENDPOINTS.STORAGE_SUPPLIER_RETURN), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          returnMonth: startOfMonth(returnMonth!).toISOString(),
          category: grazinimaiCategory,
          supplier: grazinimaiSupplier,
          items
        })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const errMsg = data.errors?.length
          ? data.errors.map((e: { message: string }) => e.message).join(', ')
          : (data.message || 'Failed to register returns');
        setGrazinimaiError(errMsg);
        return;
      }
      setGrazinimaiError(null);
      await fetchGrazinimaiStorageItems(grazinimaiCategory);
      setGrazinimaiDataText('');
      setGrazinimaiColBarkodas('');
      setGrazinimaiColKiekis('');
      grazinimaiColRefs.current = { barkodas: '', kiekis: '' };
      setProcessedReturnsRaw([]);
      setProcessedReturnsGrouped([]);
      await fetchSupplierReturnLog();
      alert(t('pardavimai.grazinimaiSuccess', { count: items.length }));
    } catch (err) {
      setGrazinimaiError(err instanceof Error ? err.message : 'Failed to register returns');
    } finally {
      setGrazinimaiLoading(false);
    }
  };

  const handleProcessSalesData = () => {
    if (!prekybaCategory) {
      setPrekybaError('Pasirinkite kategoriją');
      return;
    }

    if (!salesMonth) {
      setPrekybaError('Pasirinkite mėnesį');
      return;
    }

    if (!salesDataText.trim()) {
      setPrekybaError('Įklijuokite pardavimų duomenis');
      return;
    }

    setPrekybaError(null);
    const parsed = parseSalesData(salesDataText);
    const raw = matchSalesItems(parsed, prekybaStorageItems);
    setProcessedSalesRaw(raw);
    setProcessedSalesGrouped(groupProcessedSales(raw));
  };

  const handleRegisterSales = async () => {
    if (!salesMonth) {
      setPrekybaError('Pasirinkite mėnesį');
      return;
    }

    // Validate all matched items have sufficient stock
    const insufficientStock = processedSalesRaw.filter(ps => ps.status === 'insufficient_stock');
    if (insufficientStock.length > 0) {
      const items = [...new Set(insufficientStock.map(ps => ps.preke))].join(', ');
      setPrekybaError(`Nepakanka atsargų šioms prekėms: ${items}`);
      return;
    }

    const matchedItems = processedSalesRaw.filter(ps => ps.status === 'matched' && ps.matchedItem);
    if (matchedItems.length === 0) {
      setPrekybaError('Nėra prekių, kurias būtų galima registruoti');
      return;
    }

    try {
      setPrekybaLoading(true);
      setPrekybaError(null);

      const items = buildSalesRegistrationItems(processedSalesRaw);

      const response = await fetch(getApiUrl(API_ENDPOINTS.STORAGE_SALES_REGISTRATION), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          salesMonth: startOfMonth(salesMonth!).toISOString(),
          category: prekybaCategory,
          items
        })
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const errMsg = data.errors?.length
          ? data.errors.map((e: { message: string }) => e.message).join(', ')
          : (data.message || 'Failed to register sales');
        setPrekybaError(errMsg);
        return;
      }

      setPrekybaError(null);
      await fetchPrekybaStorageItems(prekybaCategory);
      const parsed = parseSalesData(salesDataText);
      const raw = matchSalesItems(parsed, prekybaStorageItems);
      setProcessedSalesRaw(raw);
      setProcessedSalesGrouped(groupProcessedSales(raw));
      await fetchSalesRegistrationLog();
      alert(`Sėkmingai užregistruota ${items.length} pardavimų!`);
    } catch (err) {
      setPrekybaError(err instanceof Error ? err.message : 'Failed to register sales');
    } finally {
      setPrekybaLoading(false);
    }
  };

  const fetchSalesRegistrationLog = async () => {
    setSalesRegistrationLogLoading(true);
    setSalesRegistrationLogError(null);
    try {
      const params = new URLSearchParams();
      if (prekybaCategory) params.set('category', prekybaCategory);
      params.set('limit', '50');
      const url = `${getApiUrl(API_ENDPOINTS.STORAGE_SALES_REGISTRATION_LOG)}?${params.toString()}`;
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to load log');
      const data = await response.json();
      if (data.success && Array.isArray(data.batches)) {
        setSalesRegistrationLog(data.batches);
      }
    } catch (e) {
      setSalesRegistrationLogError(e instanceof Error ? e.message : 'Nepavyko užkrauti žurnalo');
    } finally {
      setSalesRegistrationLogLoading(false);
    }
  };

  const fetchBatchItems = async (batchId: string) => {
    if (batchItemsMap[batchId]) return;
    try {
      const response = await fetch(getApiUrl(`${API_ENDPOINTS.STORAGE_SALES_REGISTRATION_LOG}/${batchId}/items`), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to load items');
      const data = await response.json();
      if (data.success && Array.isArray(data.items)) {
        setBatchItemsMap(prev => ({ ...prev, [batchId]: data.items }));
      }
    } catch {
      setBatchItemsMap(prev => ({ ...prev, [batchId]: [] }));
    }
  };

  const handleExpandBatch = (batchId: string) => {
    if (expandedBatchId === batchId) {
      setExpandedBatchId(null);
      return;
    }
    setExpandedBatchId(batchId);
    fetchBatchItems(batchId);
  };

  const fetchRestockBatchItems = async (batchId: string) => {
    if (restockBatchItemsMap[batchId]) return;
    try {
      const response = await fetch(getApiUrl(API_ENDPOINTS.STORAGE_RESTOCK_LOG_ITEMS(batchId)), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to load items');
      const data = await response.json();
      if (data.success && Array.isArray(data.items)) {
        setRestockBatchItemsMap(prev => ({ ...prev, [batchId]: data.items }));
      }
    } catch {
      setRestockBatchItemsMap(prev => ({ ...prev, [batchId]: [] }));
    }
  };

  const handleExpandRestockBatch = (batchId: string) => {
    if (expandedRestockBatchId === batchId) {
      setExpandedRestockBatchId(null);
      return;
    }
    setExpandedRestockBatchId(batchId);
    fetchRestockBatchItems(batchId);
  };

  const fetchStoreTransferLog = async () => {
    setStoreTransferLogLoading(true);
    setStoreTransferLogError(null);
    try {
      const params = new URLSearchParams();
      params.set('limit', '50');
      params.set('category', 'souvenirs');
      const url = `${getApiUrl(API_ENDPOINTS.STORAGE_STORE_TRANSFER_LOG)}?${params.toString()}`;
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to load store transfer log');
      const data = await response.json();
      if (data.success && Array.isArray(data.batches)) {
        setStoreTransferLog(data.batches);
      }
    } catch (e) {
      setStoreTransferLogError(e instanceof Error ? e.message : 'Nepavyko užkrauti perkėlimų žurnalo');
    } finally {
      setStoreTransferLogLoading(false);
    }
  };

  const fetchStoreTransferBatchItems = async (batchId: string) => {
    if (storeTransferBatchItemsMap[batchId]) return;
    try {
      const response = await fetch(getApiUrl(API_ENDPOINTS.STORAGE_STORE_TRANSFER_LOG_ITEMS(batchId)), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to load items');
      const data = await response.json();
      if (data.success && Array.isArray(data.items)) {
        setStoreTransferBatchItemsMap(prev => ({ ...prev, [batchId]: data.items }));
      }
    } catch {
      setStoreTransferBatchItemsMap(prev => ({ ...prev, [batchId]: [] }));
    }
  };

  const handleExpandStoreTransferBatch = (batchId: string) => {
    if (expandedStoreTransferBatchId === batchId) {
      setExpandedStoreTransferBatchId(null);
      return;
    }
    setExpandedStoreTransferBatchId(batchId);
    fetchStoreTransferBatchItems(batchId);
  };

  const getStoreTransferDirectionLabel = (direction: string) =>
    direction === 'to_store' ? 'Į parduotuvę' : 'Atgal į sandėliuką';

  const getPrekybaRowColor = (status: string, kiekis?: number) => {
    if (status === 'matched' && kiekis !== undefined && kiekis < 0) {
      return '#e3f2fd'; // info.light – retract row
    }
    switch (status) {
      case 'unmatched': return '#fff3cd'; // warning.light
      case 'insufficient_stock': return '#ffcdd2'; // error.light
      case 'matched': return '#c8e6c9'; // success.light
      default: return 'inherit';
    }
  };

  // Ataskaitos functions
  const fetchSuppliersForCategory = async (category: string) => {
    if (!category) {
      setAtaskaitosSuppliers([]);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api${API_ENDPOINTS.STORAGE_ITEMS_BY_CATEGORY(category)}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          const suppliers = Array.from(
            new Set(data.items.map((item: StorageItem) => item.supplier).filter(Boolean))
          ).sort() as string[];
          setAtaskaitosSuppliers(suppliers);
        }
      }
    } catch (error) {
      console.error('Error fetching suppliers:', error);
      setAtaskaitosError('Nepavyko užkrauti tiekėjų');
    }
  };

  const handleGenerateReport = async () => {
    if (!ataskaitosMonth || !ataskaitosCategory) {
      setAtaskaitosError('Pasirinkite mėnesį ir kategoriją');
      return;
    }

    try {
      setAtaskaitosLoading(true);
      setAtaskaitosError(null);

      const monthStr = format(ataskaitosMonth, 'yyyy-MM');
      const supplierParam = ataskaitosSupplier || '__all__';
      const url = `${getApiUrl(API_ENDPOINTS.STORAGE_SALES_REPORT)}?month=${monthStr}&category=${ataskaitosCategory}&supplier=${encodeURIComponent(supplierParam)}`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to generate report');
      }

      const data = await response.json();
      if (data.success) {
        setAtaskaitosReport(data.report || []);
        setAtaskaitosVaztarasciuSarasas(data.vaztarasciuSarasas || []);
      } else {
        throw new Error('Failed to generate report');
      }
    } catch (err) {
      console.error('Error generating report:', err);
      setAtaskaitosError(err instanceof Error ? err.message : 'Nepavyko sugeneruoti ataskaitos');
      setAtaskaitosReport([]);
      setAtaskaitosVaztarasciuSarasas([]);
    } finally {
      setAtaskaitosLoading(false);
    }
  };

  const handleCopyReportToExcel = async () => {
    if (ataskaitosReport.length === 0) {
      return;
    }

    try {
      const showSupplierCol = ataskaitosSupplier === '__all__' || !ataskaitosSupplier;
      const showParduotuveCol = ataskaitosCategory === 'souvenirs';
      const headers = [
        'Barkodas',
        'Fabis kodas',
        'Prekė',
        ...(showSupplierCol ? ['Tiekėjas'] : []),
        'Papildyta',
        'Parduota',
        'Paimta',
        'Grąžinta tiekėjui',
        ...(showParduotuveCol ? ['Parduotuvė'] : []),
        'Likutis',
        'Savikaina (vid.)',
        'Savikaina (viso)',
        'Kaina',
        'Pelnas'
      ];
      const headerRow = headers.join('\t');

      // Calculate totals
      const totals = ataskaitosReport.reduce((acc, item) => {
        acc.papildyta += item.papildyta || 0;
        acc.parduota += item.parduota || 0;
        acc.paimta += item.paimta || 0;
        acc.grazintaTiekejui += (item as { grazintaTiekejui?: number }).grazintaTiekejui || 0;
        if (showParduotuveCol) {
          acc.parduotuve += (item as { parduotuve?: number }).parduotuve ?? 0;
        }
        acc.likutis += item.likutis || 0;
        acc.pelnas += item.pelnas || 0;
        const viso = (item as { savikainaViso?: number | null }).savikainaViso;
        if (viso != null) acc.savikainaViso += viso;
        return acc;
      }, { papildyta: 0, parduota: 0, paimta: 0, grazintaTiekejui: 0, parduotuve: 0, likutis: 0, pelnas: 0, savikainaViso: 0 });

      // Create data rows
      const dataRows = ataskaitosReport.map(item => {
        // Format barcode as text by prefixing with single quote and equals sign to force Excel to treat as text
        // This prevents Excel from converting long numbers to scientific notation
        // Format: '="barcode" forces Excel to treat it as a text formula
        // Sanitize strings: newlines break tab-separated layout when pasted into Excel
        const barkodas = item.barkodas ? `="${(item.barkodas).replace(/\r?\n/g, '')}"` : '';
        const fabisKodas = (item as { fabisKodas?: string }).fabisKodas
          ? `="${String((item as { fabisKodas?: string }).fabisKodas).replace(/\r?\n/g, '')}"`
          : '';
        const preke = (item.preke || '').replace(/\r?\n/g, ' ');
        const tiekėjas = showSupplierCol ? ((item as { supplier?: string }).supplier || '').replace(/\r?\n/g, ' ') : null;
        const papildyta = item.papildyta || 0;
        const parduota = item.parduota || 0;
        const paimta = item.paimta || 0;
        const grazintaTiekejui = (item as { grazintaTiekejui?: number }).grazintaTiekejui || 0;
        const parduotuve = showParduotuveCol ? ((item as { parduotuve?: number }).parduotuve ?? 0) : null;
        const likutis = item.likutis || 0;
        const row = item as { savikainaAtaskaitai?: number | null; savikaina?: number | null; savikainaViso?: number | null };
        const savikainaVidDisplay = row.savikainaAtaskaitai ?? row.savikaina;
        const savikainaVid = savikainaVidDisplay !== null && savikainaVidDisplay !== undefined
          ? savikainaVidDisplay.toFixed(2).replace('.', ',')
          : '';
        const savikainaViso = row.savikainaViso !== null && row.savikainaViso !== undefined
          ? row.savikainaViso.toFixed(2).replace('.', ',')
          : '';
        const kaina = item.kaina !== null && item.kaina !== undefined 
          ? item.kaina.toFixed(2).replace('.', ',') 
          : '';
        const pelnas = item.pelnas !== null && item.pelnas !== undefined 
          ? item.pelnas.toFixed(2).replace('.', ',') 
          : '';

        const baseCols = [barkodas, fabisKodas, preke];
        if (showSupplierCol) baseCols.push(tiekėjas as string);
        const stockCols = [papildyta, parduota, paimta, grazintaTiekejui];
        if (showParduotuveCol) stockCols.push(parduotuve as number);
        return [...baseCols, ...stockCols, likutis, savikainaVid, savikainaViso, kaina, pelnas].join('\t');
      });

      // Create totals row
      const totalsRowCols = [
        'Iš viso',
        '',
        '',
        ...(showSupplierCol ? [''] : []),
        totals.papildyta,
        totals.parduota,
        totals.paimta,
        totals.grazintaTiekejui,
        ...(showParduotuveCol ? [totals.parduotuve] : []),
        totals.likutis,
        '',
        totals.savikainaViso > 0 ? totals.savikainaViso.toFixed(2).replace('.', ',') : '',
        '',
        totals.pelnas.toFixed(2).replace('.', ',')
      ];
      const totalsRow = totalsRowCols.join('\t');

      // Combine header, data rows, and totals row
      const excelData = [headerRow, ...dataRows, totalsRow].join('\n');

      // Copy to clipboard
      await navigator.clipboard.writeText(excelData);
      
      // Show success message
      alert('Ataskaita nukopijuota į iškarpinę! Galite įklijuoti į Excel.');
    } catch (err) {
      console.error('Error copying report:', err);
      setAtaskaitosError('Nepavyko nukopijuoti ataskaitos');
    }
  };

  // Pajamavimas functions
  const fetchStorageItems = async (category: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api${API_ENDPOINTS.STORAGE_ITEMS_BY_CATEGORY(category)}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setStorageItems(data.items);
          // Extract unique suppliers
          const suppliers = Array.from(new Set(data.items.map((item: StorageItem) => item.supplier).filter(Boolean))).sort() as string[];
          setAllSuppliers(suppliers);
        }
      }
    } catch (error) {
      console.error('Error fetching storage items:', error);
      setPajamavimasError('Nepavyko užkrauti prekių iš Sandėliukas');
    }
  };

  useEffect(() => {
    if (pajamavimasCategory) {
      fetchStorageItems(pajamavimasCategory);
    }
  }, [pajamavimasCategory]);

  useEffect(() => {
    pajamavimasColRefs.current.barkodas = pajamavimasColBarkodas;
    pajamavimasColRefs.current.pavadinimas = pajamavimasColPavadinimas;
    pajamavimasColRefs.current.kiekis = pajamavimasColKiekis;
    pajamavimasColRefs.current.savikaina = pajamavimasColSavikaina;
    pajamavimasColRefs.current.kaina = pajamavimasColKaina;
  }, [pajamavimasColBarkodas, pajamavimasColPavadinimas, pajamavimasColKiekis, pajamavimasColSavikaina, pajamavimasColKaina]);

  const parsePajamavimasData = (text: string): ParsedItem[] => {
    const lines = text.trim().split('\n');
    const parsed: ParsedItem[] = [];

    lines.forEach((line, index) => {
      const trimmedLine = line.trim();
      if (!trimmedLine) return;

      const parts = trimmedLine.split('\t');
      if (parts.length >= 4) {
        const fourth = parts[3].trim();
        const savikainaNum = fourth !== '' ? parseFloat(fourth) : NaN;
        const fifth = parts.length > 4 ? parts[4].trim() : '';
        const kainaNum = fifth !== '' ? parseFloat(fifth) : NaN;
        const parsedItem: ParsedItem = {
          barkodas: parts[0].trim(),
          pavadinimas: parts[1].trim(),
          papildymoKiekis: parseInt(parts[2].trim()) || 0,
          lineNumber: index + 1
        };
        if (!isNaN(savikainaNum)) {
          parsedItem.savikaina = savikainaNum;
        }
        if (!isNaN(kainaNum)) {
          parsedItem.kaina = kainaNum;
        }
        parsed.push(parsedItem);
      }
    });

    return parsed;
  };

  /** Parse a column that may contain double-quoted values (newlines inside quotes) and/or unquoted lines. Yields values in order. */
  const parseColumnWithQuotedStrings = (colText: string): string[] => {
    const trimmed = colText.trim();
    if (!trimmed) return [];
    const values: string[] = [];
    let i = 0;
    while (i < trimmed.length) {
      const rest = trimmed.slice(i);
      const leadingSpace = rest.match(/^\s+/);
      if (leadingSpace) {
        i += leadingSpace[0].length;
        continue;
      }
      if (rest.startsWith('"')) {
        const end = rest.indexOf('"', 1);
        if (end === -1) {
          values.push(rest.slice(1).trim());
          break;
        }
        values.push(rest.slice(1, end).trim());
        i += end + 1;
        continue;
      }
      const nextQuote = rest.indexOf('"');
      const nextNewline = rest.indexOf('\n');
      const end =
        nextQuote === -1 && nextNewline === -1
          ? rest.length
          : nextQuote === -1
            ? nextNewline
            : nextNewline === -1
              ? nextQuote
              : Math.min(nextQuote, nextNewline);
      const segment = rest.slice(0, end).trim();
      if (segment) values.push(segment);
      i += end === rest.length ? end : end + (rest[end] === '\n' ? 1 : 0);
    }
    if (values.length > 0) return values;
    return trimmed.split('\n').map(l => l.trim());
  };

  const buildParsedItemsFromColumns = (
    colBarkodas: string,
    colPavadinimas: string,
    colKiekis: string,
    colSavikaina: string,
    colKaina: string
  ): ParsedItem[] => {
    const barkodasLines = colBarkodas.trim().split('\n').map(l => l.trim());
    const pavadinimasLines = parseColumnWithQuotedStrings(colPavadinimas);
    const kiekisLines = colKiekis.trim().split('\n').map(l => l.trim());
    const savikainaLines = colSavikaina.trim().split('\n').map(l => l.trim());
    const kainaLines = colKaina.trim().split('\n').map(l => l.trim());
    const maxLen = Math.max(
      barkodasLines.length,
      pavadinimasLines.length,
      kiekisLines.length,
      savikainaLines.length,
      kainaLines.length
    );
    const parsed: ParsedItem[] = [];
    for (let i = 0; i < maxLen; i++) {
      const barkodas = barkodasLines[i] ?? '';
      const pavadinimas = pavadinimasLines[i] ?? '';
      if (!barkodas && !pavadinimas) continue;
      const papildymoKiekis = parseInt(kiekisLines[i]?.trim() ?? '0') || 0;
      const savikainaRaw = savikainaLines[i]?.trim() ?? '';
      const savikainaNum = savikainaRaw !== '' ? parseFloat(savikainaRaw) : NaN;
      const fifth = kainaLines[i]?.trim() ?? '';
      const kainaNum = fifth !== '' ? parseFloat(fifth) : NaN;
      const item: ParsedItem = {
        barkodas,
        pavadinimas,
        papildymoKiekis,
        lineNumber: i + 1
      };
      if (!isNaN(savikainaNum)) {
        item.savikaina = savikainaNum;
      }
      if (!isNaN(kainaNum)) {
        item.kaina = kainaNum;
      }
      parsed.push(item);
    }
    parsed.forEach((p, idx) => {
      p.lineNumber = idx + 1;
    });
    return parsed;
  };

  const matchPajamavimasItems = (parsed: ParsedItem[]): MatchedItem[] => {
    return parsed.map(item => {
      let matchedItem: StorageItem | null = null;
      let matchType: 'barkodas' | 'name' | 'none' = 'none';
      let needsPriceUpdate = false;
      let barcodeMismatch = false;

      // Match by barcode when barcode is present (no name fallback for those rows)
      if (item.barkodas) {
        matchedItem = storageItems.find(si => si.barkodas === item.barkodas) || null;
        if (matchedItem) {
          matchType = 'barkodas';
        }
      }

      // When row has no barcode, allow matching by name so Prekės pavadinimas column can resolve to existing items
      if (!matchedItem && !item.barkodas && item.pavadinimas.trim()) {
        const exactMatch = storageItems.find(si =>
          si.name.toLowerCase() === item.pavadinimas.toLowerCase()
        );
        if (exactMatch) {
          matchedItem = exactMatch;
          matchType = 'name';
        } else {
          const partialMatch = storageItems.find(si =>
            item.pavadinimas.toLowerCase().includes(si.name.toLowerCase()) ||
            si.name.toLowerCase().includes(item.pavadinimas.toLowerCase())
          );
          if (partialMatch) {
            matchedItem = partialMatch;
            matchType = 'name';
          }
        }
      }

      // Check if prices differ and store original prices
      let originalSavikaina: number | undefined;
      let originalPardavimoKaina: number | undefined;
      let savikainaChanged = false;
      let pardavimoKainaChanged = false;
      if (matchedItem) {
        // Convert to number if it's a string (DECIMAL from database might be string)
        originalSavikaina = matchedItem.savikaina !== undefined && matchedItem.savikaina !== null 
          ? typeof matchedItem.savikaina === 'string' ? parseFloat(matchedItem.savikaina) : Number(matchedItem.savikaina)
          : undefined;
        originalPardavimoKaina = matchedItem.pardavimoKaina !== undefined && matchedItem.pardavimoKaina !== null
          ? typeof matchedItem.pardavimoKaina === 'string' ? parseFloat(matchedItem.pardavimoKaina) : Number(matchedItem.pardavimoKaina)
          : undefined;
        savikainaChanged = item.savikaina !== undefined && item.savikaina !== null && !isNaN(item.savikaina) &&
          originalSavikaina !== undefined &&
          Math.abs(originalSavikaina - item.savikaina) > 0.01;
        pardavimoKainaChanged = item.kaina !== undefined && item.kaina !== null && !isNaN(item.kaina) &&
          originalPardavimoKaina !== undefined &&
          Math.abs(originalPardavimoKaina - item.kaina) > 0.01;
        needsPriceUpdate = savikainaChanged || pardavimoKainaChanged;
      }

      let status: 'matched' | 'unmatched' | 'price_update_needed' | 'ready' = 'unmatched';
      if (!matchedItem) {
        status = 'unmatched';
      } else if (needsPriceUpdate) {
        status = 'price_update_needed';
      } else {
        status = 'ready';
      }

      return {
        ...item,
        matchedItem,
        matchType,
        needsPriceUpdate,
        priceUpdateApplied: false,
        wasCreated: false,
        pendingCreation: false,
        originalSavikaina,
        originalPardavimoKaina,
        savikainaChanged,
        pardavimoKainaChanged,
        status,
        barcodeMismatch
      };
    });
  };

  const handleProcessPajamavimasData = () => {
    if (!pajamavimasCategory) {
      setPajamavimasError('Pasirinkite kategoriją');
      return;
    }

    if (!pajamavimasSupplier && !isNewSupplier) {
      setPajamavimasError('Pasirinkite arba įveskite tiekėją');
      return;
    }

    setPajamavimasError(null);

    if (pajamavimasInputMode === 'full') {
      if (!pastedData.trim()) {
        setPajamavimasError('Įklijuokite duomenis');
        return;
      }
      const parsed = parsePajamavimasData(pastedData);
      setParsedItems(parsed);
      setMatchedItems(matchPajamavimasItems(parsed));
      return;
    }

    const refs = pajamavimasColRefs.current;
    const colBarkodas = refs.barkodas !== undefined && refs.barkodas !== '' ? refs.barkodas : pajamavimasColBarkodas;
    const colPavadinimas = refs.pavadinimas !== undefined && refs.pavadinimas !== '' ? refs.pavadinimas : pajamavimasColPavadinimas;
    const colKiekis = refs.kiekis !== undefined && refs.kiekis !== '' ? refs.kiekis : pajamavimasColKiekis;
    const colSavikaina = refs.savikaina !== undefined && refs.savikaina !== '' ? refs.savikaina : pajamavimasColSavikaina;
    const colKaina = refs.kaina !== undefined && refs.kaina !== '' ? refs.kaina : pajamavimasColKaina;
    const hasColumnData =
      colBarkodas.trim() !== '' ||
      colPavadinimas.trim() !== '' ||
      colKiekis.trim() !== '' ||
      colSavikaina.trim() !== '' ||
      colKaina.trim() !== '';
    if (!hasColumnData) {
      setPajamavimasError('Įveskite bent vieną stulpelį');
      return;
    }
    const parsed = buildParsedItemsFromColumns(
      colBarkodas,
      colPavadinimas,
      colKiekis,
      colSavikaina,
      colKaina
    );
    if (parsed.length === 0) {
      setPajamavimasError('Nėra duomenų (bent Barkodas arba Prekės pavadinimas kiekvienoje eilutėje)');
      return;
    }
    setParsedItems(parsed);
    setMatchedItems(matchPajamavimasItems(parsed));
  };

  const handleCreateNewItem = (parsedItem: ParsedItem) => {
    // Just mark the item as pending creation, don't create it yet
    const updated = matchedItems.map(mi => 
      mi.lineNumber === parsedItem.lineNumber 
        ? { ...mi, pendingCreation: true, status: 'ready' as const }
        : mi
    );
    setMatchedItems(updated);
    setCreateItemDialogOpen(false);
    setItemToCreate(null);
  };

  const handleCreateAllNewItems = () => {
    const unmatchedItems = matchedItems.filter(mi => mi.status === 'unmatched');
    
    if (unmatchedItems.length === 0) {
      setPajamavimasError('Nėra naujų produktų, kuriuos reikėtų sukurti');
      return;
    }

    // Just mark all unmatched items as pending creation, don't create them yet
    const updated = matchedItems.map(mi => 
      mi.status === 'unmatched'
        ? { ...mi, pendingCreation: true, status: 'ready' as const }
        : mi
    );
    setMatchedItems(updated);
    setPajamavimasError(null);
    alert(`Pažymėta ${unmatchedItems.length} produktų sukūrimui. Spauskite "Papildyti" norint sukurti.`);
  };

  type PriceUpdateAction = 'keep' | 'savikaina' | 'kaina' | 'both';

  const handleUpdatePrice = async (matchedItem: MatchedItem, action: PriceUpdateAction) => {
    if (!matchedItem.matchedItem) return;

    const setRowReady = () => {
      const updated = matchedItems.map(mi =>
        mi.lineNumber === matchedItem.lineNumber
          ? { ...mi, priceUpdateApplied: true, status: 'ready' as const, needsPriceUpdate: false }
          : mi
      );
      setMatchedItems(updated);
    };

    if (action === 'keep') {
      setRowReady();
      setPriceMenuAnchor(null);
      return;
    }

    if (action === 'kaina' && (matchedItem.kaina === undefined || matchedItem.kaina === null)) {
      setRowReady();
      setPriceMenuAnchor(null);
      return;
    }

    if (action === 'savikaina' && (matchedItem.savikaina === undefined || matchedItem.savikaina === null)) {
      setRowReady();
      setPriceMenuAnchor(null);
      return;
    }

    try {
      setPajamavimasLoading(true);
      setPriceMenuAnchor(null);

      let body: { savikaina?: number; pardavimoKaina?: number } = {};
      if (action === 'savikaina') {
        body = { savikaina: matchedItem.savikaina };
      } else if (action === 'kaina') {
        body = { pardavimoKaina: matchedItem.kaina };
      } else {
        if (matchedItem.savikaina != null) body.savikaina = matchedItem.savikaina;
        if (matchedItem.kaina != null) body.pardavimoKaina = matchedItem.kaina;
      }

      if (Object.keys(body).length === 0) {
        setRowReady();
        setPriceMenuAnchor(null);
        return;
      }

      const response = await fetch(`${getApiUrl(API_ENDPOINTS.STORAGE_ITEMS)}/${matchedItem.matchedItem.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update prices');
      }

      await fetchStorageItems(pajamavimasCategory);
      setRowReady();
    } catch (err) {
      setPajamavimasError(err instanceof Error ? err.message : 'Failed to update prices');
    } finally {
      setPajamavimasLoading(false);
    }
  };

  const handleApplyChanges = async () => {
    try {
      setPajamavimasLoading(true);
      setPajamavimasError(null);
      const supplier = isNewSupplier ? newSupplierName : pajamavimasSupplier;
      const documentNumber = pajamavimasVaztarasitis.trim() || undefined;

      const itemsToCreate = matchedItems
        .filter(mi => mi.pendingCreation)
        .map(mi => ({
          name: mi.pavadinimas,
          category: pajamavimasCategory,
          quantity: mi.papildymoKiekis,
          supplier,
          barkodas: mi.barkodas || undefined,
          savikaina: mi.savikaina != null && !isNaN(Number(mi.savikaina)) ? Number(mi.savikaina) : null,
          pardavimoKaina: mi.kaina != null && !isNaN(Number(mi.kaina)) ? Number(mi.kaina) : null,
          unit: 'vnt',
          minQuantity: 5
        }));

      const readyItems = matchedItems.filter(mi =>
        mi.status === 'ready' && mi.matchedItem && !mi.pendingCreation
      );
      const itemsToRestock = readyItems
        .filter(mi => mi.papildymoKiekis != null && mi.papildymoKiekis > 0)
        .map(mi => ({
          itemId: mi.matchedItem!.id,
          quantity: mi.papildymoKiekis!,
          savikaina: mi.savikaina != null && !isNaN(Number(mi.savikaina)) ? Number(mi.savikaina) : (mi.matchedItem?.savikaina != null && !isNaN(Number(mi.matchedItem.savikaina)) ? Number(mi.matchedItem.savikaina) : undefined),
          notes: `Papildymas iš Pajamavimas: +${mi.papildymoKiekis} vnt`
        }));

      if (itemsToCreate.length === 0 && itemsToRestock.length === 0) {
        setPajamavimasLoading(false);
        return;
      }

      const response = await fetch(getApiUrl(API_ENDPOINTS.STORAGE_RESTOCK_BATCH), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          category: pajamavimasCategory,
          supplier,
          documentNumber,
          itemsToCreate,
          itemsToRestock
        })
      });

      const data = await response.json();

      if (!response.ok) {
        const message = data.message || 'Failed to process restock batch';
        const errors = Array.isArray(data.errors) ? data.errors.map((e: { message?: string }) => e.message).filter(Boolean).join(', ') : '';
        setPajamavimasError(errors ? `${message}: ${errors}` : message);
        return;
      }

      setPajamavimasError(null);
      const created = data.createdItemsCount || 0;
      const transactionsCount = data.transactionsCount || 0;
      const restocked = Math.max(0, transactionsCount - created);
      alert(t('pardavimai.restockSuccess', { count: transactionsCount, created, restocked }));
      setPastedData('');
      setParsedItems([]);
      setMatchedItems([]);
      await fetchStorageItems(pajamavimasCategory);
      await fetchRestockLog();
    } catch (err) {
      setPajamavimasError(err instanceof Error ? err.message : 'Failed to apply changes');
    } finally {
      setPajamavimasLoading(false);
    }
  };

  const getRowColor = (item: MatchedItem) => {
    if (item.status === 'unmatched') return '#fff3cd'; // warning.light equivalent
    if (item.barcodeMismatch) return '#ffe0b2'; // orange light for barcode mismatch
    if (item.status === 'price_update_needed') return '#ffe0b2'; // orange light
    if (item.status === 'ready' && item.pendingCreation) return '#e3f2fd'; // info.light equivalent
    if (item.status === 'ready') return '#c8e6c9'; // success.light equivalent
    return 'inherit';
  };

  // Check permissions - users need pardavimai.view permission
  const canViewPardavimai = hasPermission('pardavimai.view');
  
  if (!canViewPardavimai) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Jūs neturite prieigos prie šio skyriaus. Reikalingas leidimas: pardavimai.view
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <AssessmentIcon sx={{ mr: 2 }} />
        Prekyba
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label="Pardavimų registravimas" />
          <Tab label="Pajamavimas" />
          <Tab label={t('pardavimai.grazinimaiTab')} />
          <Tab label="Ataskaitos" />
        </Tabs>
      </Box>

      {/* Prekyba Tab */}
      {activeTab === 0 && (
        <Box>
          {prekybaError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPrekybaError(null)}>
              {prekybaError}
            </Alert>
          )}

          {/* Category Selection */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Pasirinkite kategoriją
              </Typography>
              <FormControl fullWidth required>
                <InputLabel>Kategorija *</InputLabel>
                <Select
                  value={prekybaCategory}
                  label="Kategorija *"
                  onChange={(e) => setPrekybaCategory(e.target.value)}
                >
                  <MenuItem value="business">Ūkinės prekės</MenuItem>
                  <MenuItem value="office">Kanceliarinės prekės</MenuItem>
                  <MenuItem value="exhibition">Ekspozicija</MenuItem>
                  <MenuItem value="discovery_space">Atradimų erdvė</MenuItem>
                  <MenuItem value="souvenirs">Suvenyrai</MenuItem>
                  <MenuItem value="stickers">Lipdukai</MenuItem>
                  <MenuItem value="work_tools">Darbo reikmenys</MenuItem>
                  <MenuItem value="workshop">Asmeniniai</MenuItem>
                  <MenuItem value="kitchen">Virtuvėlė</MenuItem>
                </Select>
              </FormControl>
            </CardContent>
          </Card>

          {/* Month Selection */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Pasirinkite mėnesį
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <FormControl required sx={{ minWidth: 140 }}>
                    <InputLabel>Mėnuo *</InputLabel>
                    <Select
                      label="Mėnuo *"
                      value={salesMonth ? salesMonth.getMonth() : new Date().getMonth()}
                      onChange={(e) => setSalesMonth((prev) => {
                        const y = prev ? prev.getFullYear() : new Date().getFullYear();
                        return new Date(y, Number(e.target.value), 1);
                      })}
                    >
                      {MONTH_NAMES_LT.map((name, i) => (
                        <MenuItem key={name} value={i}>{name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl required sx={{ minWidth: 100 }}>
                    <InputLabel>Metai *</InputLabel>
                    <Select
                      label="Metai *"
                      value={salesMonth ? salesMonth.getFullYear() : new Date().getFullYear()}
                      onChange={(e) => setSalesMonth((prev) => {
                        const m = prev ? prev.getMonth() : new Date().getMonth();
                        return new Date(Number(e.target.value), m, 1);
                      })}
                    >
                      {Array.from({ length: 7 }, (_, i) => new Date().getFullYear() - 5 + i).map((y) => (
                        <MenuItem key={y} value={y}>{y}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Sales Data Input */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Įklijuokite pardavimų duomenis
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Formatas: Prekės kodas [TAB] kiekis. Ta pati prekė gali būti keliose eilutėse — peržiūroje jos sujungiamos, registravime išlieka atskiros eilutės. Neigiamas kiekis = atšaukti pardavimą.
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={10}
                variant="outlined"
                placeholder="Prekės kodas	kiekis
Kojinės 000577	3
Kojinės 000577	2
Kojinės 000577	5"
                value={salesDataText}
                onChange={(e) => setSalesDataText(e.target.value)}
                sx={{ mb: 2 }}
              />
              <Button
                variant="contained"
                onClick={handleProcessSalesData}
                disabled={!prekybaCategory || !salesDataText.trim()}
                startIcon={<AssessmentIcon />}
              >
                Apdoroti duomenis
              </Button>
            </CardContent>
          </Card>

          {/* Processed Sales Table */}
          {processedSalesGrouped.length > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    Apdoroti pardavimai ({processedSalesGrouped.length} {processedSalesGrouped.length === 1 ? 'prekė' : 'prekės'})
                  </Typography>
                  <Button
                    variant="contained"
                    color="success"
                    onClick={handleRegisterSales}
                    disabled={prekybaLoading || processedSalesRaw.filter(ps => ps.status === 'matched').length === 0}
                    startIcon={prekybaLoading ? <CircularProgress size={20} /> : <CheckCircleIcon />}
                  >
                    {prekybaLoading ? 'Registruojama...' : 'Registruoti pardavimus'}
                  </Button>
                </Box>

                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Prekės kodas</TableCell>
                        <TableCell>Prekė</TableCell>
                        <TableCell>Tiekėjas</TableCell>
                        <TableCell>{usesStoreStockForSales(prekybaCategory) ? 'Parduotuvėje' : 'Dabartinis kiekis'}</TableCell>
                        <TableCell>Parduota</TableCell>
                        <TableCell>{usesStoreStockForSales(prekybaCategory) ? 'Po registravimo' : 'Naujas kiekis'}</TableCell>
                        <TableCell>Barkodas</TableCell>
                        <TableCell>Statusas</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {processedSalesGrouped.map((item, index) => (
                        <TableRow
                          key={index}
                          sx={{
                            bgcolor: getPrekybaRowColor(item.status, item.kiekis)
                          }}
                        >
                          <TableCell>{item.prekesKodas}</TableCell>
                          <TableCell>{item.preke}</TableCell>
                          <TableCell>{item.matchedItem?.supplier ?? '-'}</TableCell>
                          <TableCell>{item.currentQuantity}</TableCell>
                          <TableCell>
                            {item.kiekis}
                            {item.sourceLineCount != null && item.sourceLineCount > 1 && (
                              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                                ({item.sourceLineCount} eil.)
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>{item.newQuantity}</TableCell>
                          <TableCell>{item.barkodas || '-'}</TableCell>
                          <TableCell>
                            {item.status === 'unmatched' && (
                              <Chip label="Nerasta" color="warning" size="small" />
                            )}
                            {item.status === 'insufficient_stock' && (
                              <Chip label="Nepakanka atsargų" color="error" size="small" />
                            )}
                            {item.status === 'matched' && item.kiekis < 0 && (
                              <Chip label="Atšaukti" color="info" size="small" />
                            )}
                            {item.status === 'matched' && item.kiekis > 0 && (
                              <Chip label="Paruošta" color="success" size="small" />
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}

          {/* Registravimų žurnalas */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('pardavimai.registrationLog')}
              </Typography>
              {salesRegistrationLogError && (
                <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setSalesRegistrationLogError(null)}>
                  {salesRegistrationLogError}
                </Alert>
              )}
              {salesRegistrationLogLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={32} />
                </Box>
              ) : (
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox" sx={{ width: 48 }} />
                        <TableCell>{t('pardavimai.registrationLogMonth')}</TableCell>
                        <TableCell>{t('pardavimai.registrationLogRegisteredBy')}</TableCell>
                        <TableCell>{t('pardavimai.registrationLogSupplier')}</TableCell>
                        <TableCell>{t('pardavimai.registrationLogDate')}</TableCell>
                        <TableCell>{t('pardavimai.registrationLogItemCount')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {salesRegistrationLog.map((batch) => {
                        const [y, m] = batch.salesMonth.split('-').map(Number);
                        const monthLabel = m >= 1 && m <= 12 ? `${y} ${MONTH_NAMES_LT[m - 1]}` : batch.salesMonth;
                        const isExpanded = expandedBatchId === batch.id;
                        const items = batchItemsMap[batch.id];
                        return (
                          <React.Fragment key={batch.id}>
                            <TableRow hover>
                              <TableCell padding="checkbox">
                                <IconButton
                                  size="small"
                                  onClick={() => handleExpandBatch(batch.id)}
                                  aria-label={isExpanded ? t('pardavimai.registrationLogCollapse') : t('pardavimai.registrationLogExpand')}
                                >
                                  {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                </IconButton>
                              </TableCell>
                              <TableCell>{monthLabel}</TableCell>
                              <TableCell>
                                {batch.user ? `${batch.user.firstName} ${batch.user.lastName}` : '–'}
                              </TableCell>
                              <TableCell>{batch.supplier ?? '–'}</TableCell>
                              <TableCell>
                                {format(new Date(batch.registeredAt), 'yyyy-MM-dd HH:mm')}
                              </TableCell>
                              <TableCell>{batch.itemCount}</TableCell>
                            </TableRow>
                            {isExpanded && (
                              <TableRow>
                                <TableCell colSpan={6} sx={{ py: 0, borderBottom: 'none', bgcolor: 'action.hover' }}>
                                  <Box sx={{ px: 2, py: 1 }}>
                                    {items === undefined ? (
                                      <Typography variant="body2" color="text.secondary">{t('pardavimai.registrationLogLoading')}</Typography>
                                    ) : items.length === 0 ? (
                                      <Typography variant="body2" color="text.secondary">{t('pardavimai.registrationLogNoEntries')}</Typography>
                                    ) : (
                                      <Table size="small">
                                            <TableHead>
                                              <TableRow>
                                                <TableCell>{t('pardavimai.registrationLogProduct')}</TableCell>
                                                <TableCell>{t('pardavimai.registrationLogProductCode')}</TableCell>
                                                <TableCell>{t('pardavimai.registrationLogSupplier')}</TableCell>
                                                <TableCell>{t('pardavimai.registrationLogQuantity')}</TableCell>
                                                <TableCell>{t('pardavimai.registrationLogNote')}</TableCell>
                                              </TableRow>
                                            </TableHead>
                                            <TableBody>
                                              {items.map((row) => (
                                                <TableRow key={row.id}>
                                                  <TableCell>{row.item?.name ?? '–'}</TableCell>
                                                  <TableCell>{row.item?.prekesKodas ?? '–'}</TableCell>
                                                  <TableCell>{row.item?.supplier ?? '–'}</TableCell>
                                                  <TableCell>{row.quantity} vnt</TableCell>
                                                  <TableCell>{row.notes || '–'}</TableCell>
                                                </TableRow>
                                              ))}
                                            </TableBody>
                                          </Table>
                                    )}
                                  </Box>
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              {!salesRegistrationLogLoading && salesRegistrationLog.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                  {t('pardavimai.registrationLogEmpty')}
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* Perkėlimų žurnalas */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Perkėlimų žurnalas (Sandėliukas ↔ Parduotuvė)
              </Typography>
              {storeTransferLogError && (
                <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setStoreTransferLogError(null)}>
                  {storeTransferLogError}
                </Alert>
              )}
              {storeTransferLogLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={32} />
                </Box>
              ) : (
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox" sx={{ width: 48 }} />
                        <TableCell>Data</TableCell>
                        <TableCell>Kas</TableCell>
                        <TableCell>Kryptis</TableCell>
                        <TableCell>Prekių sk.</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {storeTransferLog.map((batch) => {
                        const isExpanded = expandedStoreTransferBatchId === batch.id;
                        const items = storeTransferBatchItemsMap[batch.id];
                        return (
                          <React.Fragment key={batch.id}>
                            <TableRow hover>
                              <TableCell padding="checkbox">
                                <IconButton size="small" onClick={() => handleExpandStoreTransferBatch(batch.id)}>
                                  {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                </IconButton>
                              </TableCell>
                              <TableCell>
                                {format(new Date(batch.registeredAt), 'yyyy-MM-dd HH:mm')}
                              </TableCell>
                              <TableCell>
                                {batch.user ? `${batch.user.firstName} ${batch.user.lastName}` : '–'}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={getStoreTransferDirectionLabel(batch.direction)}
                                  size="small"
                                  color={batch.direction === 'to_store' ? 'primary' : 'secondary'}
                                />
                              </TableCell>
                              <TableCell>{batch.itemCount}</TableCell>
                            </TableRow>
                            {isExpanded && (
                              <TableRow>
                                <TableCell colSpan={5} sx={{ py: 0, borderBottom: 0 }}>
                                  <Box sx={{ py: 2, pl: 6 }}>
                                    {!items ? (
                                      <CircularProgress size={24} />
                                    ) : items.length === 0 ? (
                                      <Typography variant="body2" color="text.secondary">Prekių nėra</Typography>
                                    ) : (
                                      <Table size="small">
                                        <TableHead>
                                          <TableRow>
                                            <TableCell>Prekė</TableCell>
                                            <TableCell>Prekės kodas</TableCell>
                                            <TableCell>Kiekis</TableCell>
                                            <TableCell>Pastaba</TableCell>
                                          </TableRow>
                                        </TableHead>
                                        <TableBody>
                                          {items.map((row) => (
                                            <TableRow key={row.id}>
                                              <TableCell>{row.item?.name ?? '–'}</TableCell>
                                              <TableCell>{row.item?.prekesKodas ?? '–'}</TableCell>
                                              <TableCell>{row.quantity} vnt</TableCell>
                                              <TableCell>{row.notes || '–'}</TableCell>
                                            </TableRow>
                                          ))}
                                        </TableBody>
                                      </Table>
                                    )}
                                  </Box>
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              {!storeTransferLogLoading && storeTransferLog.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                  Perkėlimų žurnale įrašų dar nėra
                </Typography>
              )}
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Pajamavimas Tab */}
      {activeTab === 1 && (
        <Box>
          {pajamavimasError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPajamavimasError(null)}>
              {pajamavimasError}
            </Alert>
          )}

          {/* Selection Controls */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Pasirinkite kategoriją ir tiekėją
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Važtaraštis
                </Typography>
                <TextField
                  id="pajamavimas-vaztarasitis"
                  fullWidth
                  size="small"
                  variant="outlined"
                  label="Važtaraštis (neprivaloma)"
                  value={pajamavimasVaztarasitis}
                  onChange={(e) => setPajamavimasVaztarasitis(e.target.value)}
                  placeholder="Įveskite dokumento numerį"
                />
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                <FormControl fullWidth required>
                  <InputLabel>Kategorija *</InputLabel>
                  <Select
                    value={pajamavimasCategory}
                    label="Kategorija *"
                    onChange={(e) => setPajamavimasCategory(e.target.value)}
                  >
                    <MenuItem value="business">Ūkinės prekės</MenuItem>
                    <MenuItem value="office">Kanceliarinės prekės</MenuItem>
                    <MenuItem value="exhibition">Ekspozicija</MenuItem>
                    <MenuItem value="discovery_space">Atradimų erdvė</MenuItem>
                    <MenuItem value="souvenirs">Suvenyrai</MenuItem>
                    <MenuItem value="stickers">Lipdukai</MenuItem>
                    <MenuItem value="work_tools">Darbo reikmenys</MenuItem>
                  <MenuItem value="workshop">Asmeniniai</MenuItem>
                  <MenuItem value="kitchen">Virtuvėlė</MenuItem>
                  </Select>
                </FormControl>

                <FormControl fullWidth required>
                  <InputLabel>Tiekėjas *</InputLabel>
                  <Select
                    value={isNewSupplier ? 'new' : pajamavimasSupplier}
                    label="Tiekėjas *"
                    onChange={(e) => {
                      if (e.target.value === 'new') {
                        setIsNewSupplier(true);
                        setPajamavimasSupplier('');
                      } else {
                        setIsNewSupplier(false);
                        setPajamavimasSupplier(e.target.value);
                      }
                    }}
                  >
                    {allSuppliers.map((supplier) => (
                      <MenuItem key={supplier} value={supplier}>
                        {supplier}
                      </MenuItem>
                    ))}
                    <MenuItem value="new">+ Naujas tiekėjas</MenuItem>
                  </Select>
                </FormControl>

                {isNewSupplier && (
                  <TextField
                    fullWidth
                    label="Naujo tiekėjo pavadinimas"
                    value={newSupplierName}
                    onChange={(e) => setNewSupplierName(e.target.value)}
                    sx={{ gridColumn: 'span 2' }}
                  />
                )}
              </Box>
            </CardContent>
          </Card>

          {/* Data Input Area */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Įklijuokite duomenis
              </Typography>
              <ToggleButtonGroup
                value={pajamavimasInputMode}
                exclusive
                onChange={(_, value) => value != null && setPajamavimasInputMode(value)}
                size="small"
                sx={{ mb: 2 }}
              >
                <ToggleButton value="full">Pilnas sąrašas</ToggleButton>
                <ToggleButton value="columns">Stulpeliais</ToggleButton>
              </ToggleButtonGroup>
              {pajamavimasInputMode === 'full' ? (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Formatas: Barkodas [TAB] Prekės pavadinimas [TAB] Papildymo kiekis [TAB] Savikaina [TAB] Kaina (neprivaloma)
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={10}
                    variant="outlined"
                    placeholder="Barkodas	Prekės pavadinimas	Papildymo kiekis	Savikaina	Kaina (neprivaloma)"
                    value={pastedData}
                    onChange={(e) => setPastedData(e.target.value)}
                    sx={{ mb: 2 }}
                  />
                </>
              ) : (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Viena eilutė = viena prekė. Eilučių tvarka turi sutapti visuose stulpeliuose.
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, mb: 2, overflowX: 'auto' }}>
                    <TextField
                      label="Barkodas"
                      multiline
                      rows={6}
                      variant="outlined"
                      size="small"
                      value={pajamavimasColBarkodas}
                      onChange={(e) => {
                        const v = e.target.value;
                        pajamavimasColRefs.current.barkodas = v;
                        setPajamavimasColBarkodas(v);
                      }}
                      sx={{ minWidth: 120, flex: '1 1 0' }}
                    />
                    <TextField
                      label="Prekės pavadinimas"
                      multiline
                      rows={6}
                      variant="outlined"
                      size="small"
                      value={pajamavimasColPavadinimas}
                      onChange={(e) => {
                        const v = e.target.value;
                        pajamavimasColRefs.current.pavadinimas = v;
                        setPajamavimasColPavadinimas(v);
                      }}
                      sx={{ minWidth: 120, flex: '1 1 0' }}
                    />
                    <TextField
                      label="Papildymo kiekis"
                      multiline
                      rows={6}
                      variant="outlined"
                      size="small"
                      value={pajamavimasColKiekis}
                      onChange={(e) => {
                        const v = e.target.value;
                        pajamavimasColRefs.current.kiekis = v;
                        setPajamavimasColKiekis(v);
                      }}
                      sx={{ minWidth: 120, flex: '1 1 0' }}
                    />
                    <TextField
                      label="Savikaina"
                      multiline
                      rows={6}
                      variant="outlined"
                      size="small"
                      value={pajamavimasColSavikaina}
                      onChange={(e) => {
                        const v = e.target.value;
                        pajamavimasColRefs.current.savikaina = v;
                        setPajamavimasColSavikaina(v);
                      }}
                      sx={{ minWidth: 120, flex: '1 1 0' }}
                    />
                    <TextField
                      label="Kaina (neprivaloma)"
                      multiline
                      rows={6}
                      variant="outlined"
                      size="small"
                      value={pajamavimasColKaina}
                      onChange={(e) => {
                        const v = e.target.value;
                        pajamavimasColRefs.current.kaina = v;
                        setPajamavimasColKaina(v);
                      }}
                      sx={{ minWidth: 120, flex: '1 1 0' }}
                    />
                  </Box>
                </>
              )}
              <Button
                variant="contained"
                onClick={handleProcessPajamavimasData}
                disabled={
                  pajamavimasInputMode === 'full'
                    ? !pastedData.trim()
                    : !pajamavimasColBarkodas.trim() &&
                      !pajamavimasColPavadinimas.trim() &&
                      !pajamavimasColKiekis.trim() &&
                      !pajamavimasColSavikaina.trim() &&
                      !pajamavimasColKaina.trim()
                }
                startIcon={<AssessmentIcon />}
              >
                Apdoroti duomenis
              </Button>
            </CardContent>
          </Card>

          {/* Processed Items Table */}
          {matchedItems.length > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                  <Typography variant="h6">
                    Apdoroti duomenys ({matchedItems.length} eilutės)
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    {matchedItems.filter(mi => mi.status === 'unmatched').length > 0 && (
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={handleCreateAllNewItems}
                        disabled={pajamavimasLoading}
                        startIcon={pajamavimasLoading ? <CircularProgress size={20} /> : <AddIcon />}
                      >
                        {pajamavimasLoading ? 'Kuriama...' : `Visi nauji produktai (${matchedItems.filter(mi => mi.status === 'unmatched').length})`}
                      </Button>
                    )}
                    <Button
                      variant="contained"
                      color="success"
                      onClick={handleApplyChanges}
                      disabled={pajamavimasLoading || (matchedItems.filter(mi => mi.pendingCreation).length === 0 && matchedItems.filter(mi => mi.status === 'ready' && mi.matchedItem && !mi.pendingCreation && (mi.papildymoKiekis ?? 0) > 0).length === 0)}
                      startIcon={pajamavimasLoading ? <CircularProgress size={20} /> : <CheckCircleIcon />}
                    >
                      {pajamavimasLoading ? 'Apdorojama...' : 'Papildyti'}
                    </Button>
                  </Box>
                </Box>

                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Barkodas</TableCell>
                        <TableCell>Prekės pavadinimas</TableCell>
                        <TableCell>Papildymo kiekis</TableCell>
                        <TableCell>Savikaina</TableCell>
                        <TableCell>Kaina</TableCell>
                        <TableCell>Statusas</TableCell>
                        <TableCell>Veiksmai</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {matchedItems.map((item, index) => (
                        <TableRow
                          key={index}
                          sx={{
                            bgcolor: getRowColor(item)
                          }}
                        >
                          <TableCell>{item.barkodas || '-'}</TableCell>
                          <TableCell>{(item.pavadinimas || '').trim() || (item.matchedItem?.name ?? '')}</TableCell>
                          <TableCell>{item.papildymoKiekis}</TableCell>
                          <TableCell>
                            {item.status === 'price_update_needed' && item.savikainaChanged && item.originalSavikaina !== undefined && item.originalSavikaina !== null && !isNaN(Number(item.originalSavikaina)) ? (
                              <Box>
                                <Typography variant="body2" sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>
                                  €{Number(item.originalSavikaina).toFixed(2)}
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                                  → €{item.savikaina != null && !isNaN(item.savikaina) ? item.savikaina.toFixed(2) : '—'}
                                </Typography>
                              </Box>
                            ) : item.savikaina != null && !isNaN(item.savikaina) ? (
                              `€${item.savikaina.toFixed(2)}`
                            ) : item.matchedItem?.savikaina != null && !isNaN(Number(item.matchedItem.savikaina)) ? (
                              `€${Number(item.matchedItem.savikaina).toFixed(2)} (esama)`
                            ) : (
                              '—'
                            )}
                          </TableCell>
                          <TableCell>
                            {item.kaina != null ? (
                              item.status === 'price_update_needed' && item.pardavimoKainaChanged && item.originalPardavimoKaina !== undefined && item.originalPardavimoKaina !== null && !isNaN(Number(item.originalPardavimoKaina)) ? (
                                <Box>
                                  <Typography variant="body2" sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>
                                    €{Number(item.originalPardavimoKaina).toFixed(2)}
                                  </Typography>
                                  <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                                    → €{item.kaina.toFixed(2)}
                                  </Typography>
                                </Box>
                              ) : (
                                `€${item.kaina.toFixed(2)}`
                              )
                            ) : item.matchedItem?.pardavimoKaina != null && !isNaN(Number(item.matchedItem.pardavimoKaina)) ? (
                              `€${Number(item.matchedItem.pardavimoKaina).toFixed(2)} (esama)`
                            ) : (
                              '—'
                            )}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              {item.barcodeMismatch && (
                                <Chip label="Barkodas nesutampa" color="warning" size="small" />
                              )}
                              {item.status === 'unmatched' && (
                                <Chip label="Nerasta" color="warning" size="small" />
                              )}
                              {item.status === 'price_update_needed' && (
                                <Chip label="Reikia atnaujinti kainas" color="warning" size="small" />
                              )}
                              {item.status === 'ready' && item.pendingCreation && (
                                <Chip label="Laukia sukūrimo" color="info" size="small" />
                              )}
                              {item.status === 'ready' && !item.pendingCreation && (
                                <Chip label="Paruošta" color="success" size="small" />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            {item.status === 'unmatched' && (
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<AddIcon />}
                                onClick={() => {
                                  setItemToCreate(item);
                                  setCreateItemDialogOpen(true);
                                }}
                              >
                                Naujas produktas
                              </Button>
                            )}
                            {item.status === 'price_update_needed' && (
                              <Button
                                size="small"
                                variant="outlined"
                                color="warning"
                                startIcon={<UpdateIcon />}
                                onClick={(e) => setPriceMenuAnchor({ anchorEl: e.currentTarget, lineNumber: item.lineNumber })}
                                disabled={pajamavimasLoading}
                              >
                                Kainos
                              </Button>
                            )}
                            {item.status === 'ready' && (
                              <Chip label="Paruošta" color="success" size="small" />
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Menu
                  anchorEl={priceMenuAnchor?.anchorEl ?? null}
                  open={!!priceMenuAnchor}
                  onClose={() => setPriceMenuAnchor(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                  {priceMenuAnchor && (() => {
                    const mi = matchedItems.find(m => m.lineNumber === priceMenuAnchor.lineNumber);
                    if (!mi) return null;
                    return [
                      <MenuItem key="keep" onClick={() => handleUpdatePrice(mi, 'keep')}>
                        Palikti seną
                      </MenuItem>,
                      <MenuItem key="savikaina" onClick={() => handleUpdatePrice(mi, 'savikaina')}>
                        Atnaujinti Savikainą
                      </MenuItem>,
                      <MenuItem key="kaina" onClick={() => handleUpdatePrice(mi, 'kaina')}>
                        Atnaujinti Kainą
                      </MenuItem>,
                      <MenuItem key="both" onClick={() => handleUpdatePrice(mi, 'both')}>
                        Atnaujinti abu
                      </MenuItem>
                    ];
                  })()}
                </Menu>
              </CardContent>
            </Card>
          )}

          {/* Pajamavimo žurnalas */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('pardavimai.restockLog')}
              </Typography>
              {restockLogError && (
                <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setRestockLogError(null)}>
                  {restockLogError}
                </Alert>
              )}
              {restockLogLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={32} />
                </Box>
              ) : (
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox" sx={{ width: 48 }} />
                        <TableCell>{t('pardavimai.restockLogDate')}</TableCell>
                        <TableCell>{t('pardavimai.restockLogRegisteredBy')}</TableCell>
                        <TableCell>{t('pardavimai.restockLogCategory')}</TableCell>
                        <TableCell>{t('pardavimai.restockLogSupplier')}</TableCell>
                        <TableCell>{t('pardavimai.restockLogDocumentNumber')}</TableCell>
                        <TableCell>{t('pardavimai.restockLogItemCount')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {restockLog.map((batch) => {
                        const isExpanded = expandedRestockBatchId === batch.id;
                        const items = restockBatchItemsMap[batch.id];
                        return (
                          <React.Fragment key={batch.id}>
                            <TableRow hover>
                              <TableCell padding="checkbox">
                                <IconButton
                                  size="small"
                                  onClick={() => handleExpandRestockBatch(batch.id)}
                                  aria-label={isExpanded ? t('pardavimai.restockLogCollapse') : t('pardavimai.restockLogExpand')}
                                >
                                  {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                </IconButton>
                              </TableCell>
                              <TableCell>{format(new Date(batch.registeredAt), 'yyyy-MM-dd HH:mm')}</TableCell>
                              <TableCell>
                                {batch.user ? `${batch.user.firstName} ${batch.user.lastName}` : '–'}
                              </TableCell>
                              <TableCell>{batch.category}</TableCell>
                              <TableCell>{batch.supplier}</TableCell>
                              <TableCell>{batch.documentNumber || '–'}</TableCell>
                              <TableCell>{batch.itemCount}</TableCell>
                            </TableRow>
                            {isExpanded && (
                              <TableRow>
                                <TableCell colSpan={7} sx={{ py: 0, borderBottom: 'none', bgcolor: 'action.hover' }}>
                                  <Box sx={{ px: 2, py: 1 }}>
                                    {items === undefined ? (
                                      <Typography variant="body2" color="text.secondary">{t('pardavimai.restockLogLoading')}</Typography>
                                    ) : items.length === 0 ? (
                                      <Typography variant="body2" color="text.secondary">{t('pardavimai.restockLogNoEntries')}</Typography>
                                    ) : (
                                      <Table size="small">
                                        <TableHead>
                                          <TableRow>
                                            <TableCell>{t('pardavimai.restockLogProduct')}</TableCell>
                                            <TableCell>{t('pardavimai.restockLogProductCode')}</TableCell>
                                            <TableCell>{t('pardavimai.restockLogQuantity')}</TableCell>
                                            <TableCell>{t('pardavimai.restockLogNote')}</TableCell>
                                          </TableRow>
                                        </TableHead>
                                        <TableBody>
                                          {items.map((row) => (
                                            <TableRow key={row.id}>
                                              <TableCell>{row.item?.name ?? '–'}</TableCell>
                                              <TableCell>{row.item?.prekesKodas ?? '–'}</TableCell>
                                              <TableCell>{row.quantity} vnt</TableCell>
                                              <TableCell>{row.notes || '–'}</TableCell>
                                            </TableRow>
                                          ))}
                                        </TableBody>
                                      </Table>
                                    )}
                                  </Box>
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              {!restockLogLoading && restockLog.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                  {t('pardavimai.restockLogEmpty')}
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* Create Item Dialog */}
          <Dialog
            open={createItemDialogOpen}
            onClose={() => {
              setCreateItemDialogOpen(false);
              setItemToCreate(null);
            }}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Kurti naują prekę</DialogTitle>
            <DialogContent>
              {itemToCreate && (
                <Box sx={{ pt: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Prekė bus sukurta su šiais duomenimis:
                  </Typography>
                  <Box sx={{ mt: 2 }}>
                    <Typography><strong>Pavadinimas:</strong> {itemToCreate.pavadinimas}</Typography>
                    <Typography><strong>Barkodas:</strong> {itemToCreate.barkodas || '-'}</Typography>
                    <Typography><strong>Kategorija:</strong> {pajamavimasCategory}</Typography>
                    <Typography><strong>Tiekėjas:</strong> {isNewSupplier ? newSupplierName : pajamavimasSupplier}</Typography>
                    <Typography><strong>Pradinis kiekis:</strong> {itemToCreate.papildymoKiekis}</Typography>
                    <Typography><strong>Savikaina:</strong> {itemToCreate.savikaina != null && !isNaN(itemToCreate.savikaina) ? `€${itemToCreate.savikaina.toFixed(2)}` : '—'}</Typography>
                    <Typography><strong>Pardavimo kaina:</strong> {itemToCreate.kaina != null && !isNaN(itemToCreate.kaina) ? `€${itemToCreate.kaina.toFixed(2)}` : '—'}</Typography>
                  </Box>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => {
                setCreateItemDialogOpen(false);
                setItemToCreate(null);
              }}>
                Atšaukti
              </Button>
              <Button
                variant="contained"
                onClick={() => itemToCreate && handleCreateNewItem(itemToCreate)}
                disabled={pajamavimasLoading}
              >
                {pajamavimasLoading ? 'Kuriama...' : 'Kurti'}
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      )}

      {/* Ataskaitos Tab */}
      {/* Grąžinimai Tab */}
      {activeTab === 2 && (
        <Box>
          {grazinimaiError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setGrazinimaiError(null)}>
              {grazinimaiError}
            </Alert>
          )}

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('pardavimai.grazinimaiSelectCategoryMonth')}
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                <FormControl fullWidth required>
                  <InputLabel>Kategorija *</InputLabel>
                  <Select
                    value={grazinimaiCategory}
                    label="Kategorija *"
                    onChange={(e) => {
                      setGrazinimaiCategory(e.target.value);
                      setGrazinimaiSupplier('');
                    }}
                  >
                    <MenuItem value="business">Ūkinės prekės</MenuItem>
                    <MenuItem value="office">Kanceliarinės prekės</MenuItem>
                    <MenuItem value="exhibition">Ekspozicija</MenuItem>
                    <MenuItem value="discovery_space">Atradimų erdvė</MenuItem>
                    <MenuItem value="souvenirs">Suvenyrai</MenuItem>
                    <MenuItem value="stickers">Lipdukai</MenuItem>
                    <MenuItem value="work_tools">Darbo reikmenys</MenuItem>
                    <MenuItem value="workshop">Asmeniniai</MenuItem>
                    <MenuItem value="kitchen">Virtuvėlė</MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth required>
                  <InputLabel>Tiekėjas *</InputLabel>
                  <Select
                    value={grazinimaiSupplier}
                    label="Tiekėjas *"
                    onChange={(e) => setGrazinimaiSupplier(e.target.value)}
                    disabled={!grazinimaiCategory}
                  >
                    {grazinimaiSuppliers.map((supplier) => (
                      <MenuItem key={supplier} value={supplier}>{supplier}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Box sx={{ display: 'flex', gap: 2, gridColumn: { md: '1 / -1' } }}>
                  <FormControl fullWidth required>
                    <InputLabel>Mėnuo *</InputLabel>
                    <Select
                      value={returnMonth ? returnMonth.getMonth() : new Date().getMonth()}
                      label="Mėnuo *"
                      onChange={(e) => {
                        const month = Number(e.target.value);
                        const year = returnMonth ? returnMonth.getFullYear() : new Date().getFullYear();
                        setReturnMonth(new Date(year, month, 1));
                      }}
                    >
                      {MONTH_NAMES_LT.map((name, i) => (
                        <MenuItem key={name} value={i}>{name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl fullWidth required>
                    <InputLabel>Metai *</InputLabel>
                    <Select
                      value={returnMonth ? returnMonth.getFullYear() : new Date().getFullYear()}
                      label="Metai *"
                      onChange={(e) => {
                        const year = Number(e.target.value);
                        const month = returnMonth ? returnMonth.getMonth() : new Date().getMonth();
                        setReturnMonth(new Date(year, month, 1));
                      }}
                    >
                      {Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - 2 + i).map((y) => (
                        <MenuItem key={y} value={y}>{y}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('pardavimai.grazinimaiPasteLabel')}
              </Typography>
              <ToggleButtonGroup
                value={grazinimaiInputMode}
                exclusive
                onChange={(_, value) => value != null && setGrazinimaiInputMode(value)}
                size="small"
                sx={{ mb: 2 }}
              >
                <ToggleButton value="full">{t('pardavimai.grazinimaiModeFull')}</ToggleButton>
                <ToggleButton value="columns">{t('pardavimai.grazinimaiModeColumns')}</ToggleButton>
              </ToggleButtonGroup>
              {grazinimaiInputMode === 'full' ? (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {t('pardavimai.grazinimaiPasteHint')}
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    minRows={6}
                    label={t('pardavimai.grazinimaiPasteLabel')}
                    value={grazinimaiDataText}
                    onChange={(e) => setGrazinimaiDataText(e.target.value)}
                    placeholder={"1234567890123\t2\n9876543210987\t1"}
                  />
                </>
              ) : (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {t('pardavimai.grazinimaiColumnsHint')}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, mb: 1, overflowX: 'auto' }}>
                    <TextField
                      label={t('pardavimai.grazinimaiColBarkodas')}
                      multiline
                      rows={6}
                      variant="outlined"
                      size="small"
                      value={grazinimaiColBarkodas}
                      onChange={(e) => {
                        const v = e.target.value;
                        grazinimaiColRefs.current.barkodas = v;
                        setGrazinimaiColBarkodas(v);
                      }}
                      sx={{ minWidth: 160, flex: '1 1 0' }}
                    />
                    <TextField
                      label={t('pardavimai.grazinimaiColKiekis')}
                      multiline
                      rows={6}
                      variant="outlined"
                      size="small"
                      value={grazinimaiColKiekis}
                      onChange={(e) => {
                        const v = e.target.value;
                        grazinimaiColRefs.current.kiekis = v;
                        setGrazinimaiColKiekis(v);
                      }}
                      sx={{ minWidth: 120, flex: '1 1 0' }}
                    />
                  </Box>
                </>
              )}
              <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handleProcessGrazinimaiData}
                  disabled={
                    !grazinimaiCategory ||
                    !grazinimaiSupplier ||
                    (grazinimaiInputMode === 'full'
                      ? !grazinimaiDataText.trim()
                      : !grazinimaiColBarkodas.trim() && !grazinimaiColKiekis.trim())
                  }
                >
                  {t('pardavimai.grazinimaiProcess')}
                </Button>
                <Button
                  variant="contained"
                  onClick={handleRegisterGrazinimai}
                  disabled={
                    grazinimaiLoading ||
                    processedReturnsRaw.length === 0 ||
                    processedReturnsRaw.every(ps => ps.status !== 'matched')
                  }
                >
                  {grazinimaiLoading ? <CircularProgress size={22} color="inherit" /> : t('pardavimai.grazinimaiRegister')}
                </Button>
              </Box>
            </CardContent>
          </Card>

          {processedReturnsGrouped.length > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('pardavimai.grazinimaiTitle')}
                </Typography>
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('pardavimai.grazinimaiColBarkodas')}</TableCell>
                        <TableCell>{t('pardavimai.registrationLogProduct')}</TableCell>
                        <TableCell>{t('pardavimai.registrationLogSupplier')}</TableCell>
                        <TableCell>{t('pardavimai.grazinimaiStockInWarehouse')}</TableCell>
                        <TableCell>{t('pardavimai.grazinimaiReturnedQty')}</TableCell>
                        <TableCell>{t('pardavimai.grazinimaiNewQty')}</TableCell>
                        <TableCell>Statusas</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {processedReturnsGrouped.map((item) => (
                        <TableRow
                          key={`${item.lineNumber}-${item.barkodas || item.prekesKodas}`}
                          sx={{
                            bgcolor:
                              item.status === 'unmatched' ? 'error.light' :
                              item.status === 'insufficient_stock' ? 'warning.light' :
                              undefined
                          }}
                        >
                          <TableCell>{item.barkodas || item.prekesKodas}</TableCell>
                          <TableCell>{item.preke}</TableCell>
                          <TableCell>{item.matchedItem?.supplier ?? '-'}</TableCell>
                          <TableCell>{item.currentQuantity}</TableCell>
                          <TableCell>{item.kiekis}</TableCell>
                          <TableCell>{item.newQuantity}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={
                                item.status === 'matched' ? 'OK' :
                                item.status === 'insufficient_stock' ? 'Nepakanka' :
                                'Nerasta'
                              }
                              color={
                                item.status === 'matched' ? 'success' :
                                item.status === 'insufficient_stock' ? 'warning' :
                                'error'
                              }
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('pardavimai.grazinimaiRegistrationLog')}
              </Typography>
              {supplierReturnLogError && (
                <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setSupplierReturnLogError(null)}>
                  {supplierReturnLogError}
                </Alert>
              )}
              {supplierReturnLogLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={32} />
                </Box>
              ) : (
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox" sx={{ width: 48 }} />
                        <TableCell>{t('pardavimai.registrationLogMonth')}</TableCell>
                        <TableCell>{t('pardavimai.registrationLogRegisteredBy')}</TableCell>
                        <TableCell>{t('pardavimai.registrationLogSupplier')}</TableCell>
                        <TableCell>{t('pardavimai.registrationLogDate')}</TableCell>
                        <TableCell>{t('pardavimai.registrationLogItemCount')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {supplierReturnLog.map((batch) => {
                        const [y, m] = batch.returnMonth.split('-').map(Number);
                        const monthLabel = m >= 1 && m <= 12 ? `${y} ${MONTH_NAMES_LT[m - 1]}` : batch.returnMonth;
                        const isExpanded = expandedSupplierReturnBatchId === batch.id;
                        const items = supplierReturnBatchItemsMap[batch.id];
                        return (
                          <React.Fragment key={batch.id}>
                            <TableRow hover>
                              <TableCell padding="checkbox">
                                <IconButton
                                  size="small"
                                  onClick={() => handleExpandSupplierReturnBatch(batch.id)}
                                  aria-label={isExpanded ? t('pardavimai.registrationLogCollapse') : t('pardavimai.registrationLogExpand')}
                                >
                                  {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                </IconButton>
                              </TableCell>
                              <TableCell>{monthLabel}</TableCell>
                              <TableCell>
                                {batch.user ? `${batch.user.firstName} ${batch.user.lastName}` : '–'}
                              </TableCell>
                              <TableCell>{batch.supplier ?? '–'}</TableCell>
                              <TableCell>
                                {format(new Date(batch.registeredAt), 'yyyy-MM-dd HH:mm')}
                              </TableCell>
                              <TableCell>{batch.itemCount}</TableCell>
                            </TableRow>
                            {isExpanded && (
                              <TableRow>
                                <TableCell colSpan={6} sx={{ py: 0, borderBottom: 'none', bgcolor: 'action.hover' }}>
                                  <Box sx={{ px: 2, py: 1 }}>
                                    {items === undefined ? (
                                      <Typography variant="body2" color="text.secondary">{t('pardavimai.registrationLogLoading')}</Typography>
                                    ) : items.length === 0 ? (
                                      <Typography variant="body2" color="text.secondary">{t('pardavimai.registrationLogNoEntries')}</Typography>
                                    ) : (
                                      <Table size="small">
                                        <TableHead>
                                          <TableRow>
                                            <TableCell>{t('pardavimai.registrationLogProduct')}</TableCell>
                                            <TableCell>{t('pardavimai.registrationLogProductCode')}</TableCell>
                                            <TableCell>{t('pardavimai.registrationLogSupplier')}</TableCell>
                                            <TableCell>{t('pardavimai.registrationLogQuantity')}</TableCell>
                                            <TableCell>{t('pardavimai.registrationLogNote')}</TableCell>
                                          </TableRow>
                                        </TableHead>
                                        <TableBody>
                                          {items.map((row) => (
                                            <TableRow key={row.id}>
                                              <TableCell>{row.item?.name ?? '–'}</TableCell>
                                              <TableCell>{row.item?.prekesKodas ?? '–'}</TableCell>
                                              <TableCell>{row.item?.supplier ?? '–'}</TableCell>
                                              <TableCell>{row.quantity} vnt</TableCell>
                                              <TableCell>{row.notes || '–'}</TableCell>
                                            </TableRow>
                                          ))}
                                        </TableBody>
                                      </Table>
                                    )}
                                  </Box>
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              {!supplierReturnLogLoading && supplierReturnLog.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                  {t('pardavimai.grazinimaiLogEmpty')}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Box>
      )}

      {activeTab === 3 && (
        <Box>
          {ataskaitosError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setAtaskaitosError(null)}>
              {ataskaitosError}
            </Alert>
          )}

          {/* Selection Controls */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Pasirinkite parametrus
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <FormControl required sx={{ minWidth: 140 }}>
                    <InputLabel>Mėnuo *</InputLabel>
                    <Select
                      label="Mėnuo *"
                      value={ataskaitosMonth ? ataskaitosMonth.getMonth() : 0}
                      onChange={(e) => setAtaskaitosMonth((prev) => {
                        const y = prev ? prev.getFullYear() : new Date().getFullYear();
                        return new Date(y, Number(e.target.value), 1);
                      })}
                    >
                      {MONTH_NAMES_LT.map((name, i) => (
                        <MenuItem key={name} value={i}>{name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl required sx={{ minWidth: 100 }}>
                    <InputLabel>Metai *</InputLabel>
                    <Select
                      label="Metai *"
                      value={ataskaitosMonth ? ataskaitosMonth.getFullYear() : new Date().getFullYear()}
                      onChange={(e) => setAtaskaitosMonth((prev) => {
                        const m = prev ? prev.getMonth() : 0;
                        return new Date(Number(e.target.value), m, 1);
                      })}
                    >
                      {Array.from({ length: 7 }, (_, i) => new Date().getFullYear() - 5 + i).map((y) => (
                        <MenuItem key={y} value={y}>{y}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                <FormControl fullWidth required>
                  <InputLabel>Kategorija *</InputLabel>
                  <Select
                    value={ataskaitosCategory}
                    label="Kategorija *"
                    onChange={(e) => {
                      setAtaskaitosCategory(e.target.value);
                      setAtaskaitosSupplier('__all__');
                      setAtaskaitosReport([]);
                      setAtaskaitosVaztarasciuSarasas([]);
                    }}
                  >
                    <MenuItem value="business">Ūkinės prekės</MenuItem>
                    <MenuItem value="office">Kanceliarinės prekės</MenuItem>
                    <MenuItem value="exhibition">Ekspozicija</MenuItem>
                    <MenuItem value="discovery_space">Atradimų erdvė</MenuItem>
                    <MenuItem value="souvenirs">Suvenyrai</MenuItem>
                    <MenuItem value="stickers">Lipdukai</MenuItem>
                    <MenuItem value="work_tools">Darbo reikmenys</MenuItem>
                  <MenuItem value="workshop">Asmeniniai</MenuItem>
                  <MenuItem value="kitchen">Virtuvėlė</MenuItem>
                  </Select>
                </FormControl>

                <FormControl fullWidth required>
                  <InputLabel>Tiekėjas *</InputLabel>
                  <Select
                    value={ataskaitosSupplier || '__all__'}
                    label="Tiekėjas *"
                    onChange={(e) => {
                      setAtaskaitosSupplier(e.target.value);
                      setAtaskaitosReport([]);
                      setAtaskaitosVaztarasciuSarasas([]);
                    }}
                    disabled={!ataskaitosCategory}
                  >
                    <MenuItem value="__all__">{t('pardavimai.ataskaitosAllSuppliers')}</MenuItem>
                    {ataskaitosSuppliers.map((supplier) => (
                      <MenuItem key={supplier} value={supplier}>
                        {supplier}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Button
                variant="contained"
                onClick={handleGenerateReport}
                disabled={!ataskaitosMonth || !ataskaitosCategory || ataskaitosLoading}
                startIcon={ataskaitosLoading ? <CircularProgress size={20} /> : <AssessmentIcon />}
                sx={{ mt: 2 }}
              >
                {ataskaitosLoading ? 'Generuojama...' : 'Generuoti ataskaitą'}
              </Button>
            </CardContent>
          </Card>

          {/* Report Results */}
          {ataskaitosReport.length > 0 && (
            <Card>
              <CardContent>
                {ataskaitosVaztarasciuSarasas.length > 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Važtaraščiai šiame laikotarpyje: {ataskaitosVaztarasciuSarasas.join(', ')}
                  </Typography>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    Ataskaitos rezultatai
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<ContentCopyIcon />}
                    onClick={handleCopyReportToExcel}
                    size="small"
                  >
                    Kopijuoti į Excel
                  </Button>
                </Box>
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Barkodas</TableCell>
                        <TableCell>Fabis kodas</TableCell>
                        <TableCell>Prekė</TableCell>
                        {(ataskaitosSupplier === '__all__' || !ataskaitosSupplier) && <TableCell>Tiekėjas</TableCell>}
                        <TableCell>Papildyta</TableCell>
                        <TableCell>Parduota</TableCell>
                        <TableCell>Paimta</TableCell>
                        <TableCell>Grąžinta tiekėjui</TableCell>
                        {ataskaitosCategory === 'souvenirs' && <TableCell>Parduotuvė</TableCell>}
                        <TableCell>Likutis</TableCell>
                        <TableCell>Savikaina (vid.)</TableCell>
                        <TableCell>Savikaina (viso)</TableCell>
                        <TableCell>Kaina</TableCell>
                        <TableCell>Pelnas</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {ataskaitosReport.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>{item.barkodas || '-'}</TableCell>
                          <TableCell>{(item as { fabisKodas?: string }).fabisKodas || '-'}</TableCell>
                          <TableCell>{item.preke}</TableCell>
                          {(ataskaitosSupplier === '__all__' || !ataskaitosSupplier) && <TableCell>{(item as { supplier?: string }).supplier || '-'}</TableCell>}
                          <TableCell>{item.papildyta || 0}</TableCell>
                          <TableCell>{item.parduota}</TableCell>
                          <TableCell>{item.paimta || 0}</TableCell>
                          <TableCell>{(item as { grazintaTiekejui?: number }).grazintaTiekejui || 0}</TableCell>
                          {ataskaitosCategory === 'souvenirs' && (
                            <TableCell>{(item as { parduotuve?: number }).parduotuve ?? 0}</TableCell>
                          )}
                          <TableCell>{item.likutis}</TableCell>
                          <TableCell>
                            {(() => {
                              const row = item as { savikainaAtaskaitai?: number | null; savikaina?: number | null };
                              const effectiveSavikaina = row.savikainaAtaskaitai ?? row.savikaina;
                              if (effectiveSavikaina !== null && effectiveSavikaina !== undefined) {
                                const text = `${effectiveSavikaina.toFixed(2)} EUR`;
                                return row.savikainaAtaskaitai != null ? (
                                  <Tooltip title="FIFO vidurkis šiam laikotarpiui" placement="top">
                                    <span>{text}</span>
                                  </Tooltip>
                                ) : text;
                              }
                              return '-';
                            })()}
                          </TableCell>
                          <TableCell>
                            {(() => {
                              const viso = (item as { savikainaViso?: number | null }).savikainaViso;
                              return viso !== null && viso !== undefined ? `${viso.toFixed(2)} EUR` : '-';
                            })()}
                          </TableCell>
                          <TableCell>
                            {item.kaina !== null && item.kaina !== undefined
                              ? `${item.kaina.toFixed(2)} EUR`
                              : '-'}
                          </TableCell>
                          <TableCell>
                            {item.pelnas !== null && item.pelnas !== undefined
                              ? `${item.pelnas.toFixed(2)} EUR`
                              : '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                      {/* Totals Row */}
                      {ataskaitosReport.length > 0 && (() => {
                        const showParduotuveCol = ataskaitosCategory === 'souvenirs';
                        const totals = ataskaitosReport.reduce((acc, item) => {
                          acc.papildyta += item.papildyta || 0;
                          acc.parduota += item.parduota || 0;
                          acc.paimta += item.paimta || 0;
                          acc.grazintaTiekejui += (item as { grazintaTiekejui?: number }).grazintaTiekejui || 0;
                          if (showParduotuveCol) {
                            acc.parduotuve += (item as { parduotuve?: number }).parduotuve ?? 0;
                          }
                          acc.likutis += item.likutis || 0;
                          acc.pelnas += item.pelnas || 0;
                          const viso = (item as { savikainaViso?: number | null }).savikainaViso;
                          if (viso != null) acc.savikainaViso += viso;
                          return acc;
                        }, { papildyta: 0, parduota: 0, paimta: 0, grazintaTiekejui: 0, parduotuve: 0, likutis: 0, pelnas: 0, savikainaViso: 0 });

                        const showSupplierCol = ataskaitosSupplier === '__all__' || !ataskaitosSupplier;
                        return (
                          <TableRow sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>
                            <TableCell colSpan={showSupplierCol ? 4 : 3}><strong>Iš viso</strong></TableCell>
                            <TableCell><strong>{totals.papildyta}</strong></TableCell>
                            <TableCell><strong>{totals.parduota}</strong></TableCell>
                            <TableCell><strong>{totals.paimta ?? 0}</strong></TableCell>
                            <TableCell><strong>{totals.grazintaTiekejui}</strong></TableCell>
                            {showParduotuveCol && (
                              <TableCell><strong>{totals.parduotuve}</strong></TableCell>
                            )}
                            <TableCell><strong>{totals.likutis}</strong></TableCell>
                            <TableCell></TableCell>
                            <TableCell><strong>{totals.savikainaViso > 0 ? `${totals.savikainaViso.toFixed(2)} EUR` : ''}</strong></TableCell>
                            <TableCell></TableCell>
                            <TableCell><strong>{totals.pelnas.toFixed(2)} EUR</strong></TableCell>
                          </TableRow>
                        );
                      })()}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}
        </Box>
      )}
    </Box>
  );
};

export default Pardavimai;
